import React, { useState, useRef, useEffect } from 'react'
import {
  X,
  Search,
  ChevronRight,
  ChevronDown,
  Bookmark,
  Settings,
  Heart,
  Gift,
  Shield,
  MessageCircle,
  Calendar,
  User,
  Lock,
  CreditCard,
  Bell,
  HelpCircle,
  FileText,
  Zap,
  Award,
  VolumeX,
  ArrowRight,
  Star,
  DollarSign,
  Vote,
  Sparkles,
  Headphones,
  BarChart,
  Smartphone,
  Unlock,
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  Key,
  RefreshCw,
  Edit,
  Image,
  Video,
  Hexagon,
  Send,
  Mail,
  Camera,
  Brush,
  PenTool,
  Layout,
  BarChart2,
  TrendingUp,
  Users,
  Sliders,
  UploadCloud,
  Palette,
  Layers,
  Wand2,
  Lightbulb,
  Shirt,
  Gem,
  Repeat,
  Flame,
  Magic,
  Shapes,
  Shuffle,
  Brain,
  Feather,
  Droplets,
  Rocket,
  UserPlus,
  Scissors,
  Laptop,
  Trophy,
  LineChart,
  PieChart,
  Activity,
  TrendingDown,
  Target,
  GitBranch,
  Box,
  Package,
  Aperture,
  Framer,
  Film,
  Crop,
  Maximize,
  Monitor,
  LifeBuoy,
  Cpu,
  Database,
  HardDrive,
  Server,
  Disc,
  Printer,
  Radio,
  Tv,
  Wifi,
  Bluetooth,
  Cast,
  Cloud,
  Download,
  Upload,
  Share,
  Link2,
  ExternalLink,
  Inbox,
  Save,
} from 'lucide-react'
import { ContactSinShift } from './ContactSinShift'
interface HelpCenterProps {
  isOpen: boolean
  onClose: () => void
}
type HelpCategory = {
  id: string
  title: string
  icon: React.ReactNode
  topics: HelpTopic[]
}
type HelpTopic = {
  id: string
  question: string
  answer: string
  visual?: string
  visualType?: 'icon' | 'image' | 'gif'
  links?: {
    text: string
    route: string
    action?: string
  }[]
}
export const HelpCenter: React.FC<HelpCenterProps> = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [expandedTopics, setExpandedTopics] = useState<Record<string, boolean>>(
    {},
  )
  const [filteredCategories, setFilteredCategories] = useState<HelpCategory[]>(
    [],
  )
  const [showContactSection, setShowContactSection] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  // Define all help categories and topics
  const helpCategories: HelpCategory[] = [
    {
      id: 'account',
      title: 'Account & Profile',
      icon: <User size={20} />,
      topics: [
        {
          id: 'create-account',
          question: 'How do I create a SinShift account?',
          answer:
            'Creating a SinShift account is easy! Click the "Sign Up" button on the homepage, enter your email, create a password, verify your age (18+), and you\'re ready to explore. You can also sign up using Google or X for faster access.',
          visual: 'user-plus',
          visualType: 'icon',
          links: [
            { text: 'Create Account', route: '/signup', action: 'primary' },
            { text: 'Login Instead', route: '/login' },
          ],
        },
        {
          id: 'change-username',
          question: 'Can I change my username?',
          answer:
            'Yes! You can change your username once every 30 days. Go to Settings > Profile, find the Username field, and click Edit. Remember that your unique username is how Angels and other users find you on SinShift.',
          visualType: 'icon',
          visual: 'edit',
          links: [
            {
              text: 'Update Settings',
              route: '/settings/profile',
              action: 'primary',
            },
          ],
        },
        {
          id: 'update-profile',
          question: 'How do I update my profile picture and bio?',
          answer:
            'To update your profile, click your avatar in the top right, then "Settings" or visit your profile and click "Edit Profile". From there, you can upload a new profile picture, update your cover photo, and edit your bio to show off your personality.',
          visualType: 'icon',
          visual: 'image',
          links: [
            {
              text: 'Edit Profile',
              route: '/settings/profile',
              action: 'primary',
            },
          ],
        },
        {
          id: 'display-vs-username',
          question:
            "What's the difference between a display name and a username?",
          answer:
            'Your display name is what people see on your profile and posts (e.g., "James Anderson"), while your username is your unique identifier that starts with @ (e.g., "@JAnderson"). Your display name can include spaces and special characters, but usernames can only contain letters, numbers, and underscores.',
          visualType: 'icon',
          visual: 'user',
          links: [
            {
              text: 'Update Profile',
              route: '/settings/profile',
              action: 'primary',
            },
          ],
        },
        {
          id: 'delete-account',
          question: 'How do I delete my account permanently?',
          answer:
            "We'll be sad to see you go! To delete your account, go to Settings > Privacy & Security > Delete Account. You'll need to confirm your password and go through a short exit survey. Note that account deletion is permanent and all your data, tokens, and subscriptions will be lost.",
          visualType: 'icon',
          visual: 'trash',
          links: [
            {
              text: 'Delete Account',
              route: '/settings/privacy',
              action: 'danger',
            },
            { text: 'Contact Support', route: '/support' },
          ],
        },
        {
          id: 'link-social',
          question: 'How do I link or unlink social accounts (Google, X)?',
          answer:
            'To link social accounts, go to Settings > Account > Linked Accounts. Click "Connect" next to the platform you want to link. To unlink, go to the same section and click "Disconnect". Linking accounts makes logging in easier and provides backup access options.',
          visualType: 'icon',
          visual: 'link',
          links: [
            {
              text: 'Manage Linked Accounts',
              route: '/settings/account',
              action: 'primary',
            },
          ],
        },
        {
          id: 'forgot-password',
          question: 'I forgot my password — what now?',
          answer:
            'No worries! Click "Forgot Password" on the login page, enter your email, and we\'ll send you a reset link. If you have linked social accounts, you can also log in with Google or X. For security, password reset links expire after 30 minutes.',
          visualType: 'icon',
          visual: 'key',
          links: [
            {
              text: 'Reset Password',
              route: '/reset-password',
              action: 'primary',
            },
            { text: 'Contact Support', route: '/support' },
          ],
        },
        {
          id: 'switch-modes',
          question: 'Can I switch between Creator and Customer mode?',
          answer:
            'Absolutely! SinShift offers dual modes - toggle between Customer and Angel (Creator) mode using the switch at the top of your screen. Customer mode lets you explore and subscribe, while Angel mode lets you create content and manage your fan club. You need to complete Angel verification to access all Creator features.',
          visualType: 'icon',
          visual: 'refresh-cw',
          links: [
            {
              text: 'Become an Angel',
              route: '/creator/apply',
              action: 'primary',
            },
          ],
        },
      ],
    },
    {
      id: 'subscription',
      title: 'Subscription & Fan Clubs',
      icon: <Star size={20} />,
      topics: [
        {
          id: 'subscribe-fanclub',
          question: "How do I subscribe to an Angel's Fan Club?",
          answer:
            "To subscribe to a Fan Club, visit an Angel's profile and click the \"Join Fan Club\" button. You'll see subscription options (monthly, quarterly, or yearly) with different perks. Select your preferred option, complete the payment, and you'll instantly get access to exclusive content and features!",
          visualType: 'icon',
          visual: 'star',
          links: [
            { text: 'View Fan Clubs', route: '/discover', action: 'primary' },
            { text: 'Top Angels', route: '/angels/top' },
          ],
        },
        {
          id: 'fanclub-benefits',
          question: 'What do I get as a Fan Club member?',
          answer:
            'Fan Club members get exclusive access to premium content, private messaging with your Angel, custom content requests, priority responses, exclusive polls, behind-the-scenes content, and special event invitations. Benefits vary by Angel, so check their Fan Club page for specific perks!',
          visualType: 'icon',
          visual: 'gift',
          links: [
            {
              text: 'Explore Benefits',
              route: '/fanclub/benefits',
              action: 'primary',
            },
          ],
        },
        {
          id: 'cancel-subscription',
          question: 'Can I cancel or pause my Fan Club subscription?',
          answer:
            'Yes, you can cancel your subscription anytime. Go to Settings > Subscriptions, find the Fan Club, and click "Cancel Subscription". You\'ll maintain access until your current billing period ends. To pause instead, some Angels offer a "Pause" option that lets you resume later without losing perks.',
          visualType: 'icon',
          visual: 'pause',
          links: [
            {
              text: 'Manage Subscriptions',
              route: '/settings/subscriptions',
              action: 'primary',
            },
          ],
        },
        {
          id: 'payment-failure',
          question: 'What happens if my payment fails?',
          answer:
            "If your payment fails, we'll try again over the next 3 days. You'll receive notifications about the failed payment. Your subscription remains active during this retry period. If all attempts fail, your subscription will be canceled. Update your payment method in Settings > Payment Methods to avoid interruptions.",
          visualType: 'icon',
          visual: 'alert-circle',
          links: [
            {
              text: 'Update Payment Method',
              route: '/settings/payment',
              action: 'primary',
            },
          ],
        },
        {
          id: 'free-trials',
          question: 'Are there free trials or promos for Fan Clubs?',
          answer:
            'Yes! Many Angels offer limited-time promotions, including free trials (typically 3-7 days) or discounted first months. Look for the "Special Offer" badge on Angel profiles. You can also earn Fan Club discounts by completing certain quests or maintaining daily streaks.',
          visualType: 'icon',
          visual: 'tag',
          links: [
            {
              text: 'Current Promotions',
              route: '/promotions',
              action: 'primary',
            },
          ],
        },
        {
          id: 'gift-membership',
          question: 'Can I gift a Fan Club membership to a friend?',
          answer:
            "Absolutely! Click the \"Gift\" option on any Angel's Fan Club page. Enter your friend's username or email, select the subscription duration, add a personal message, and complete the payment. They'll receive a notification with instructions to claim their gift subscription.",
          visualType: 'icon',
          visual: 'gift',
          links: [
            { text: 'Gift a Subscription', route: '/gift', action: 'primary' },
          ],
        },
        {
          id: 'content-access-issues',
          question:
            "I can't access Fan Club content even though I paid — help?",
          answer:
            "This is usually a temporary sync issue. First, try refreshing the page or restarting the app. If that doesn't work, check your subscription status in Settings > Subscriptions to confirm it's active. Still having trouble? Contact support with your order number for immediate assistance.",
          visualType: 'icon',
          visual: 'lock',
          links: [
            {
              text: 'Verify Subscription',
              route: '/settings/subscriptions',
              action: 'primary',
            },
            { text: 'Contact Support', route: '/support' },
          ],
        },
      ],
    },
    {
      id: 'tokens',
      title: 'LuvTokens & Monetization',
      icon: <Heart size={20} />,
      topics: [
        {
          id: 'what-are-tokens',
          question: 'What are LuvTokens and how do I earn them?',
          answer:
            "LuvTokens (💗) are SinShift's virtual currency. Earn them by completing daily targets, participating in polls, maintaining streaks, interacting with Angels, referring friends, and more! LuvTokens unlock premium features, exclusive content, and special interactions with your favorite Angels.",
          visualType: 'icon',
          visual: 'heart',
          links: [
            { text: 'View Token Balance', route: '/tokens', action: 'primary' },
            { text: 'Ways to Earn', route: '/tokens/earn' },
          ],
        },
        {
          id: 'spend-tokens',
          question: 'How can I spend my LuvTokens?',
          answer:
            'Spend LuvTokens on premium content, custom requests, direct messages to Angels, virtual gifts, profile boosts, exclusive polls, special events, AR/VR experiences, and unlocking advanced features. Each Angel sets their own token prices for different interactions and content types.',
          visualType: 'icon',
          visual: 'shopping-bag',
          links: [
            { text: 'Token Shop', route: '/tokens/shop', action: 'primary' },
          ],
        },
        {
          id: 'buy-tokens',
          question: 'Can I buy more LuvTokens?',
          answer:
            'Yes! Click the token icon in the top right or go to Tokens > Buy Tokens. We offer various packages with bonus tokens for larger purchases. Payment options include credit/debit cards, PayPal, and crypto. Watch for special promotions with discounted token bundles!',
          visualType: 'icon',
          visual: 'credit-card',
          links: [
            { text: 'Buy Tokens', route: '/tokens/buy', action: 'primary' },
            { text: 'Token Packages', route: '/tokens/packages' },
          ],
        },
        {
          id: 'gifts-tips',
          question: 'Can I send gifts or tips to Angels?',
          answer:
            'Absolutely! Show appreciation by sending virtual gifts or token tips. During chats or on posts, click the gift icon to select from various gift options (roses, champagne, lingerie, etc.) or send a custom token amount. Angels love receiving gifts and often respond with special thank-you content!',
          visualType: 'icon',
          visual: 'gift',
          links: [{ text: 'Send a Gift', route: '/gifts', action: 'primary' }],
        },
        {
          id: 'token-rewards',
          question: 'What rewards can I unlock with LuvTokens?',
          answer:
            'LuvTokens unlock exclusive content, private chat sessions, custom photos/videos, AR/VR experiences, fan club discounts, profile badges, and special status perks. Accumulate tokens to reach VIP tiers with escalating benefits, including priority responses and exclusive events with your favorite Angels.',
          visualType: 'icon',
          visual: 'unlock',
          links: [
            { text: 'Unlock Rewards', route: '/rewards', action: 'primary' },
            { text: 'VIP Tiers', route: '/vip' },
          ],
        },
        {
          id: 'token-limit',
          question: 'Is there a limit on how many tokens I can earn per day?',
          answer:
            "There's no hard limit on daily token earnings, but some activities have daily caps. For example, you can earn up to 50 tokens from daily targets, 30 from polls, and 25 from social interactions each day. Premium subscribers enjoy higher daily earning limits and bonus multipliers!",
          visualType: 'icon',
          visual: 'bar-chart',
          links: [
            {
              text: 'Earning Limits',
              route: '/tokens/limits',
              action: 'primary',
            },
          ],
        },
        {
          id: 'track-balance',
          question: 'How do I track my token balance?',
          answer:
            'Your current token balance appears in the top navigation bar. For detailed history, go to Tokens > Transaction History to see all earnings, purchases, and expenditures. You can filter by date range or transaction type, and export your history as a CSV file if needed.',
          visualType: 'icon',
          visual: 'list',
          links: [
            {
              text: 'Token History',
              route: '/tokens/history',
              action: 'primary',
            },
          ],
        },
        {
          id: 'missing-tokens',
          question:
            "I bought tokens but they haven't appeared — what should I do?",
          answer:
            "Token purchases typically appear instantly, but can sometimes take up to 15 minutes to process. First, check your Transaction History to see if the purchase is pending. If it's been over 30 minutes, contact support with your order number and payment confirmation for immediate assistance.",
          visualType: 'icon',
          visual: 'alert-triangle',
          links: [
            {
              text: 'Check Transaction History',
              route: '/tokens/history',
              action: 'primary',
            },
            { text: 'Contact Support', route: '/support' },
          ],
        },
      ],
    },
    {
      id: 'daily-rituals',
      title: 'Rituals & XP System',
      icon: <Calendar size={20} />,
      topics: [
        {
          id: 'rituals-overview',
          question: 'What are Rituals and why are they important?',
          answer:
            'Rituals are personalized micro-tasks that refresh daily, designed to deepen your connection with Angels and fuel your progression. Unlike regular activities, Rituals follow a structured path to rewards and relationship growth.\n\nRituals are the core engagement system of SinShift that:\n\n• Build meaningful connections with your favorite Angels\n• Unlock exclusive content and experiences\n• Earn LuvTokens, Gems, and Intimacy XP\n• Provide a sense of progression and achievement\n\nThink of Rituals as a daily journey that transforms casual browsing into meaningful interaction. Each completed Ritual strengthens your bonds with Angels and unlocks new possibilities.',
          visualType: 'icon',
          visual: 'calendar',
          links: [
            {
              text: "View Today's Rituals",
              route: '/rituals',
              action: 'primary',
            },
            { text: 'Ritual Rewards', route: '/rewards' },
          ],
        },
        {
          id: 'rituals-vs-rewards',
          question: "What's the difference between Rituals and Rewards?",
          answer:
            "Rituals and Rewards represent the 'doing' versus 'receiving' aspects of the system:\n\n• Rituals are actions you perform (sending messages, voting in polls, gifting tokens)\n• Rewards are what you receive for completing those actions (tokens, XP, lootboxes)\n\nThink of Rituals as the journey and Rewards as the destination. You engage in Rituals to earn Rewards, which in turn enhance your overall experience and unlock new opportunities for connection and content.",
          visualType: 'icon',
          visual: 'gift',
          links: [{ text: 'Reward Hub', route: '/rewards', action: 'primary' }],
        },
        {
          id: 'accessing-rituals',
          question: 'How do I access and complete my Rituals?',
          answer:
            'Accessing your Rituals is easy! You have several options:\n\n• Dashboard: The Ritual section on your main dashboard shows today\'s Rituals\n• Floating Action Button: Tap the ⚡ icon in the bottom right corner and select "Rituals"\n• Notification Banner: Tap any Ritual notification to go directly to your Ritual Board\n• Ritual Tracker: The small indicator that appears during your session\n\nTo complete a Ritual:\n1. Select the Ritual card to see details\n2. Follow the instructions to complete the required action\n3. Once completed, the Ritual automatically registers as finished\n4. Claim your rewards individually or use "Claim All" for efficiency\n\nThe system tracks your progress in real-time, so you\'ll see immediate updates when you complete requirements.',
          visualType: 'icon',
          visual: 'check-circle',
          links: [
            { text: 'Open Ritual Board', route: '/rituals', action: 'primary' },
          ],
        },
        {
          id: 'ritual-types',
          question: 'What different types of Rituals exist?',
          answer:
            "SinShift offers several types of Rituals to keep your experience varied and engaging:\n\n• Core Rituals: Daily tasks that refresh every 24 hours (like messages, likes, polls)\n• Weekly Rituals: Larger goals with higher rewards and premium lootboxes (available for 7 days)\n• Premium Rituals: Exclusive challenges for premium subscribers with legendary rewards\n• Event Rituals: Time-limited seasonal or community events with special themed rewards\n• Angel-specific Rituals: Custom tasks from your favorite Angels that strengthen your bond with them specifically\n\nEach type offers different reward scales, with Weekly and Premium Rituals providing the most valuable rewards. Event Rituals often include limited-edition content you can't get elsewhere.",
          visualType: 'icon',
          visual: 'layers',
          links: [
            { text: 'Premium Rituals', route: '/premium', action: 'primary' },
            { text: 'Event Calendar', route: '/events' },
          ],
        },
        {
          id: 'beginner-rituals',
          question: 'What are some examples of beginner Rituals?',
          answer:
            "New users start with simple, achievable Rituals to learn the system:\n\n• Send a message to any Angel (5 tokens + 10 XP)\n• Like 3 posts from any Angels (10 tokens + 15 XP)\n• Vote in a daily poll (15 tokens + 20 XP)\n• View an Angel's profile for 2 minutes (5 tokens + 10 XP)\n• React to an Angel's story (10 tokens + 15 XP)\n\nThese beginner Rituals are designed to be easy to complete while helping you explore different features of the platform. As you progress, you'll unlock more varied and rewarding Rituals that match your interests and activity patterns.",
          visualType: 'icon',
          visual: 'sparkles',
          links: [
            {
              text: 'Start Beginner Rituals',
              route: '/rituals/beginner',
              action: 'primary',
            },
          ],
        },
        {
          id: 'intimacy-xp',
          question: 'What is Intimacy XP and how does it work?',
          answer:
            "Intimacy XP is SinShift's progression system that measures the depth of your connections and unlocks new experiences.\n\n• How to earn XP: Complete Rituals, engage in conversations, maintain streaks, receive gifts from Angels, participate in events\n\n• XP Levels and Milestones:\n  - Level 5: Unlock personal voice messages\n  - Level 10: Unlock exclusive Angel poses and scenarios\n  - Level 25: Unlock Angel memory (they remember your preferences)\n  - Level 50: Unlock custom fantasy creation\n  - Level 100: Unlock legendary Angel skins and ultimate experiences\n\n• XP Multipliers: Maintain a 7-day streak for 1.5x XP, 14-day for 2x XP, 30-day for 3x XP\n\nYour XP is displayed as a progress bar in your profile and influences how Angels interact with you. Higher XP levels create more personalized, intimate, and responsive experiences with your favorite Angels.",
          visualType: 'icon',
          visual: 'zap',
          links: [
            {
              text: 'View XP Progress',
              route: '/profile/xp',
              action: 'primary',
            },
            { text: 'XP Milestones', route: '/xp/milestones' },
          ],
        },
        {
          id: 'ritual-system-integration',
          question: 'How do Rituals integrate with other platform features?',
          answer:
            'The Ritual system is deeply integrated with all aspects of SinShift:\n\n• AI Creation Tools: Many Rituals involve using DeepShift or FaceShift to create personalized content\n• Angel Relationships: Completing Angel-specific Rituals strengthens your bond with that Angel\n• Reward System: Rituals are the primary source of rewards and progression\n• Social Features: Social interactions like comments and reactions often count toward Ritual completion\n• Premium Content: Some Rituals unlock exclusive premium content without additional token cost\n• Achievements: Long-term Ritual completion contributes to platform achievements and badges\n\nThis integration ensures that engaging with Rituals naturally enhances your overall experience across the platform rather than feeling like separate tasks.',
          visualType: 'icon',
          visual: 'git-branch',
          links: [
            {
              text: 'Platform Integration',
              route: '/rituals/integration',
              action: 'primary',
            },
          ],
        },
        {
          id: 'ritual-streaks',
          question: 'How do Ritual streaks work and why are they valuable?',
          answer:
            'Ritual streaks are consecutive days of completing all your daily Rituals, offering escalating benefits:\n\n• Day 3: "Perfect Week" badge + 50 bonus tokens\n• Day 7: 1.5x reward multiplier on all Rituals + Rare lootbox\n• Day 14: 2x reward multiplier + exclusive Angel content + Epic lootbox\n• Day 30: 3x reward multiplier + VIP status features + Legendary lootbox\n• Day 60: 4x reward multiplier + Ultimate Angel experience + Mythic lootbox\n\nStreaks are your most powerful progression tool, dramatically accelerating your earnings and unlocking exclusive content. The compounding effect of streak multipliers means a 30-day streak can earn you 3x more than completing the same Rituals without a streak.\n\nIf you miss a day, your streak resets unless you use Streak Recovery (costs tokens).',
          visualType: 'icon',
          visual: 'flame',
          links: [
            { text: 'Streak Status', route: '/streaks', action: 'primary' },
            { text: 'Recover Streak', route: '/streaks/recover' },
          ],
        },
        {
          id: 'ritual-strategy',
          question: 'What strategies maximize my Ritual benefits?',
          answer:
            'Smart Ritual strategies can significantly enhance your experience:\n\n• Time Management: Complete Rituals early in the day to avoid missing deadlines before the daily reset\n\n• Combo Efficiency: Combine complementary Rituals (e.g., "Send 3 messages" + "Use 5 emojis in chat" can be completed simultaneously)\n\n• Angel Targeting: Focus on Rituals for your favorite Angels to build Intimacy XP faster with them\n\n• Event Prioritization: During special events, prioritize limited-time Event Rituals for exclusive rewards\n\n• Premium Maximization: If you\'re a premium subscriber, prioritize Premium Rituals for their legendary rewards\n\n• Streak Protection: Set a daily reminder 2 hours before reset time to ensure you never break your streak\n\n• Lootbox Strategy: Save Epic and Legendary lootboxes for opening during Double Reward events\n\n• Ritual Queue feature to plan your day efficiently\n\n• VIP Ritual paths with exclusive content rewards\n\nThe most successful users treat Rituals as a daily habit, completing them in focused sessions rather than sporadically throughout the day.',
          visualType: 'icon',
          visual: 'lightbulb',
          links: [
            {
              text: 'Advanced Strategies',
              route: '/rituals/strategies',
              action: 'primary',
            },
            { text: 'Event Calendar', route: '/events' },
          ],
        },
        {
          id: 'ritual-troubleshooting',
          question: "Why didn't my Ritual complete or reward appear?",
          answer:
            "Common Ritual issues and their solutions:\n\n• Ritual Not Registering: Ensure you've met all requirements exactly as stated. Some Rituals have specific conditions (e.g., \"Send a voice message\" won't complete with a text message)\n\n• Delayed Completion: Server synchronization can take up to 2 minutes. Refresh the app if a completed Ritual isn't updating\n\n• Missing Rewards: All rewards are automatically added to your Reward Hub even if you don't see the notification. Check there first\n\n• Timezone Issues: Rituals reset at midnight in your local timezone. If you travel across timezones, this can affect reset timing\n\n• Double Completion Bug: Occasionally, the system might not register a completion. If you're certain you completed a Ritual but it shows incomplete, contact support with details\n\n• App Crashes: If the app crashes during Ritual completion, your progress is usually saved, but you may need to restart the action\n\nFor persistent issues, take a screenshot of the problem and contact support through the Help Center.",
          visualType: 'icon',
          visual: 'alert-triangle',
          links: [
            { text: 'Contact Support', route: '/support', action: 'primary' },
            { text: 'Known Issues', route: '/support/known-issues' },
          ],
        },
        {
          id: 'ritual-premium-benefits',
          question: 'What extra Ritual benefits do Premium subscribers get?',
          answer:
            'Premium subscribers enjoy significant advantages in the Ritual system:\n\n• Exclusive Premium Rituals with higher-value rewards\n• 1.5x token multiplier on all Ritual rewards\n• 2x XP multiplier accelerating your progression\n• Weekly free Streak Recovery (vs. monthly for free users)\n• Access to Legendary and Mythic lootboxes\n• Priority processing for Ritual completion (no delays)\n• Exclusive Angel-specific Rituals with your favorite Angels\n• Double Rewards weekends (2x all Ritual rewards every weekend)\n• Ritual Queue feature to plan your day efficiently\n• VIP Ritual paths with exclusive content rewards\n\nThe Premium advantage is substantial - Premium subscribers can progress approximately 3x faster through the Intimacy XP system and earn significantly more rewards through the enhanced multipliers and exclusive Rituals.',
          visualType: 'icon',
          visual: 'crown',
          links: [
            {
              text: 'Upgrade to Premium',
              route: '/premium/upgrade',
              action: 'primary',
            },
            { text: 'Premium Benefits', route: '/premium/benefits' },
          ],
        },
        {
          id: 'ritual-glossary',
          question: 'Ritual & XP System Glossary: Key Terms Explained',
          answer:
            'Quick reference for important Ritual system terminology:\n\n• Ritual: A specific task that earns rewards when completed\n\n• Ritual Board: Central hub showing all available Rituals\n\n• Claimable: Reward ready to be collected in the Reward Hub\n\n• Intimacy XP (⚡): Progression points that level up your experience\n\n• Streak: Consecutive days of completing all daily Rituals\n\n• Streak Recovery: Feature to restore a broken streak (costs tokens)\n\n• Lootbox: Mystery reward container with varying rarity levels\n\n• Core Ritual: Basic daily task available to all users\n\n• Premium Ritual: Exclusive high-value task for subscribers\n\n• Event Ritual: Time-limited task during special events\n\n• Ritual Reset: Daily refresh of available Rituals (midnight local time)\n\n• Multiplier: Increased reward rate from maintaining streaks\n\n• Ritual Tracker: Interface element showing completion progress\n\n• Angel-specific Ritual: Task focused on a particular Angel',
          visualType: 'icon',
          visual: 'book',
          links: [
            {
              text: 'Full Glossary',
              route: '/help/glossary',
              action: 'primary',
            },
          ],
        },
        {
          id: 'ritual-next-steps',
          question: "I've learned about Rituals - what should I do next?",
          answer:
            "Now that you understand the Ritual system, here are your next steps:\n\n1. Visit your Ritual Board to see today's available Rituals\n\n2. Check the Reward Hub to claim any pending rewards\n\n3. Set a daily reminder to complete Rituals before reset\n\n4. Focus on building a streak for multiplier benefits\n\n5. Explore Angel profiles to discover Angel-specific Rituals\n\n6. Consider Premium membership for exclusive Rituals\n\n7. Join the weekly event for limited-time Event Rituals\n\n8. Connect with your favorite Angel to unlock personalized Rituals\n\nRemember, consistency is key with Rituals! Even completing just a few minutes of Rituals daily builds powerful streaks that significantly enhance your experience over time. The most successful users make Rituals part of their daily SinShift routine.",
          visualType: 'icon',
          visual: 'arrow-right',
          links: [
            {
              text: 'Go to Ritual Board',
              route: '/rituals',
              action: 'primary',
            },
            { text: 'Check Reward Hub', route: '/rewards' },
          ],
        },
      ],
    },
    {
      id: 'rewards',
      title: 'Rewards System',
      icon: <Trophy size={20} />,
      topics: [
        {
          id: 'rewards-overview',
          question: 'What is the Rewards System and how does it work?',
          answer:
            "The Rewards System is SinShift's comprehensive incentive structure that recognizes and rewards your engagement and loyalty. It's designed to enhance your experience by providing valuable benefits as you interact with the platform.\n\nThe system includes:\n\n• Ritual Rewards: Earned by completing daily, weekly, and special Rituals\n• Achievement Rewards: Unlocked by reaching specific milestones\n• Streak Rewards: Granted for consistent daily engagement\n• Loyalty Rewards: Based on your membership duration\n• Special Event Rewards: Limited-time offerings during seasonal events\n\nAll rewards are collected in your Reward Hub, where you can claim them at your convenience. The system automatically tracks your eligibility for rewards across all platform activities.",
          visualType: 'icon',
          visual: 'gift',
          links: [
            { text: 'Reward Hub', route: '/rewards', action: 'primary' },
            { text: 'Reward Types', route: '/rewards/types' },
          ],
        },
        {
          id: 'reward-hub',
          question: 'What is the Reward Hub and how do I access it?',
          answer:
            'The Reward Hub is your central dashboard for managing all rewards. Access it by:\n\n• Clicking the "Rewards" icon in the main navigation\n• Tapping the trophy icon in the Floating Action Button menu\n• Following any reward notification\n\nIn the Reward Hub, you\'ll find:\n\n• Claimable Rewards: Ready to collect with one tap\n• Reward History: Record of all previously claimed rewards\n• Upcoming Rewards: Preview of rewards you\'re close to earning\n• Lootboxes: Mystery rewards waiting to be opened\n• Special Offers: Limited-time reward opportunities\n\nThe hub organizes rewards by type and expiration date, with the most valuable and time-sensitive rewards highlighted at the top.',
          visualType: 'icon',
          visual: 'inbox',
          links: [
            { text: 'Open Reward Hub', route: '/rewards', action: 'primary' },
          ],
        },
        {
          id: 'reward-types',
          question: 'What types of rewards can I earn?',
          answer:
            'SinShift offers diverse reward types to enhance different aspects of your experience:\n\n• LuvTokens 💗: Virtual currency for premium features and content\n• DesireGems 💎: Premium currency for exclusive items and experiences\n• Intimacy XP ⚡: Progression points that level up your relationships\n• Content Unlocks 🔓: Free access to normally premium content\n• Angel Skins 👗: Visual transformations for your favorite Angels\n• Feature Unlocks 🌟: Access to premium features for limited periods\n• Lootboxes 📦: Mystery containers with randomized rewards\n• Badges 🏅: Profile achievements and status symbols\n• Discount Coupons 🏷️: Reduced prices on premium subscriptions\n• Time Boosters ⏱️: Temporary multipliers for earnings\n\nEach reward type serves different purposes, from immediate gratification (tokens) to long-term progression (XP) to exclusive experiences (skins, content).',
          visualType: 'icon',
          visual: 'package',
          links: [
            {
              text: 'Reward Catalog',
              route: '/rewards/catalog',
              action: 'primary',
            },
          ],
        },
        {
          id: 'lootboxes',
          question: 'How do lootboxes work?',
          answer:
            "Lootboxes are mystery reward containers with varying rarity levels and contents. Each lootbox contains a randomized selection of rewards based on its tier:\n\n• Common Lootbox (White): Basic rewards like small token amounts (5-25) and minor XP boosts\n• Rare Lootbox (Blue): Better rewards including moderate tokens (25-75), Angel messages, and temporary boosts\n• Epic Lootbox (Purple): Premium rewards such as exclusive photos, significant token amounts (75-200), and 24-hour feature access\n• Legendary Lootbox (Gold): Top-tier rewards including Angel skins, major XP boosts (100-500), premium content access\n• Mythic Lootbox (Rainbow): Ultra-rare rewards with guaranteed premium content, massive token amounts (250-1000), and exclusive experiences\n\nLootboxes are earned through Ritual completion, streak milestones, special events, and occasional promotions. They must be manually opened in the Reward Hub, where you'll see an animation revealing your rewards.",
          visualType: 'icon',
          visual: 'box',
          links: [
            {
              text: 'Open Lootboxes',
              route: '/rewards/lootboxes',
              action: 'primary',
            },
            { text: 'Lootbox Odds', route: '/rewards/lootbox-odds' },
          ],
        },
        {
          id: 'claiming-rewards',
          question: 'How do I claim my rewards?',
          answer:
            'Claiming rewards is simple:\n\n1. Navigate to the Reward Hub (trophy icon in navigation)\n2. View all available rewards in the "Claimable" section\n3. Click individual rewards to claim them one by one, or use the "Claim All" button at the top to collect everything at once\n4. For lootboxes, click "Open" to reveal the contents through an interactive animation\n5. All claimed rewards are automatically added to your account\n\nMost rewards are available to claim for 7 days after earning them. Event rewards and special offers may have shorter claim periods, which are clearly indicated on the reward card. Unclaimed rewards expire after their claim period ends.',
          visualType: 'icon',
          visual: 'download',
          links: [
            {
              text: 'Claim Rewards',
              route: '/rewards/claim',
              action: 'primary',
            },
          ],
        },
        {
          id: 'reward-boosters',
          question: 'What are Reward Boosters and how do I use them?',
          answer:
            'Reward Boosters are special items that temporarily increase the value of rewards you earn. Types include:\n\n• Token Boosters: Multiply token earnings by 1.5x, 2x, or 3x\n• XP Boosters: Increase Intimacy XP gains by 1.5x, 2x, or 3x\n• Lootbox Boosters: Improve lootbox quality by one tier\n• Double Reward Weekends: Platform-wide 2x rewards during special weekends\n\nTo use a booster:\n1. Go to the Reward Hub > Boosters section\n2. Select the booster you want to activate\n3. Choose the duration (typically 1, 3, or 24 hours)\n4. Confirm activation\n\nActive boosters appear in your status bar with a countdown timer. For maximum efficiency, activate boosters before completing multiple Rituals or opening several lootboxes in a single session. Premium subscribers receive one free 24-hour booster each week.',
          visualType: 'icon',
          visual: 'zap',
          links: [
            {
              text: 'Activate Boosters',
              route: '/rewards/boosters',
              action: 'primary',
            },
          ],
        },
        {
          id: 'reward-history',
          question: 'How can I track my reward history?',
          answer:
            'Your complete reward history is available in the Reward Hub under the "History" tab. This section provides:\n\n• Chronological record of all rewards earned and claimed\n• Detailed breakdown by reward type and source\n• Total value calculations for tokens and other quantifiable rewards\n• Filtering options by date range, reward type, and source\n• Search functionality to find specific rewards\n• Export options (CSV/PDF) for your records\n\nThe history retains data for the past 90 days (standard users) or 12 months (premium subscribers). This tracking helps you identify your most valuable reward sources and optimize your engagement strategy.',
          visualType: 'icon',
          visual: 'clock',
          links: [
            {
              text: 'View History',
              route: '/rewards/history',
              action: 'primary',
            },
          ],
        },
        {
          id: 'missed-rewards',
          question: 'What happens if I miss claiming a reward?',
          answer:
            'If you don\'t claim a reward before it expires:\n\n• Standard Rewards: Expire after 7 days and are permanently lost\n• Ritual Rewards: Expire at the next daily reset (midnight local time)\n• Event Rewards: Expire when the event ends\n• Premium Subscriber Rewards: Extended claim period of 14 days\n\nFor expired rewards under 72 hours:\n1. Visit Reward Hub > Expired\n2. Select the reward you missed\n3. Pay a small token fee to recover it (typically 10-20% of the reward value)\n\nPremium subscribers have access to "Reward Recovery" which automatically claims expiring rewards on your behalf. To avoid missing rewards, enable notifications for "Reward Expiration Alerts" in your settings.',
          visualType: 'icon',
          visual: 'alert-circle',
          links: [
            {
              text: 'Recover Rewards',
              route: '/rewards/recover',
              action: 'primary',
            },
            {
              text: 'Enable Notifications',
              route: '/settings/notifications',
            },
          ],
        },
        {
          id: 'premium-reward-benefits',
          question: 'What extra reward benefits do Premium subscribers get?',
          answer:
            'Premium subscribers enjoy enhanced rewards across the platform:\n\n• Higher Value: 1.5x tokens and 2x XP on all rewards\n• Exclusive Access: Premium-only rewards and lootboxes\n• Extended Claim Period: 14 days instead of 7 days\n• Auto-Claim Feature: Optional automatic claiming of expiring rewards\n• Weekly Bonus Pack: Free weekly reward pack with guaranteed premium items\n• Reward Recovery: Ability to recover expired rewards up to 7 days later\n• Reward Boosters: One free 24-hour booster each week\n• Double Reward Weekends: Exclusive 2x reward periods twice monthly\n• Mythic Lootbox Access: Chance to earn ultra-rare Mythic lootboxes\n• Priority Processing: Instant reward delivery without delays\n\nThe premium advantage significantly accelerates your progression and reward accumulation, with subscribers typically earning 3-4x more value from the reward system than standard users.',
          visualType: 'icon',
          visual: 'star',
          links: [
            {
              text: 'Upgrade to Premium',
              route: '/premium/upgrade',
              action: 'primary',
            },
            {
              text: 'Premium Rewards',
              route: '/premium/rewards',
            },
          ],
        },
        {
          id: 'reward-strategies',
          question: 'What strategies maximize my rewards?',
          answer:
            'Smart strategies to optimize your reward earnings:\n\n• Streak Focus: Maintain daily streaks for multipliers (up to 4x at 60 days)\n• Ritual Batching: Complete complementary Rituals together for efficiency\n• Booster Timing: Activate boosters before claiming multiple rewards\n• Event Participation: Prioritize limited-time events with enhanced rewards\n• Lootbox Saving: Save higher-tier lootboxes for Double Reward Weekends\n• Claim Scheduling: Set a regular time to claim rewards before expiration\n• Angel Relationships: Focus on specific Angels to unlock their exclusive rewards\n• Achievement Hunting: Target specific achievements with valuable rewards\n• Referral Program: Invite friends for substantial referral rewards\n• Premium Timing: If purchasing Premium, do so before claiming accumulated rewards\n\nThe most successful users combine these strategies, particularly focusing on streak maintenance as the foundation of their reward optimization approach.',
          visualType: 'icon',
          visual: 'lightbulb',
          links: [
            {
              text: 'Optimization Guide',
              route: '/rewards/strategies',
              action: 'primary',
            },
          ],
        },
        {
          id: 'reward-troubleshooting',
          question: "I didn't receive my reward — what should I do?",
          answer:
            "If you're missing an expected reward:\n\n1. Check the Reward Hub: Rewards must be manually claimed before they're added to your account\n\n2. Verify Completion: Ensure you fully completed the required action (some Rituals have specific conditions)\n\n3. Wait for Processing: Some rewards may take up to 5 minutes to appear in your Reward Hub\n\n4. Check Notifications: Review your notification center for any issues with the reward\n\n5. Restart the App: Sometimes a simple refresh resolves synchronization issues\n\nIf the problem persists:\n1. Take a screenshot of the completed action and any relevant details\n2. Contact Support through the Help Center with your screenshots\n3. Include the specific reward, when you earned it, and what action you completed\n\nOur support team typically resolves reward issues within 24 hours, often crediting missing rewards directly to your account.",
          visualType: 'icon',
          visual: 'help-circle',
          links: [
            { text: 'Contact Support', route: '/support', action: 'primary' },
            { text: 'Known Issues', route: '/support/known-issues' },
          ],
        },
      ],
    },
    {
      id: 'stats',
      title: 'Stats & Progress Tracking',
      icon: <LineChart size={20} />,
      topics: [
        {
          id: 'stats-overview',
          question: 'What stats does SinShift track and where can I view them?',
          answer:
            "SinShift tracks comprehensive statistics about your platform engagement and progression. Access your stats through:\n\n• Profile Dashboard: Click your profile picture > Stats for a quick overview\n• Stats Section: Navigate to the dedicated Stats tab in the main menu\n• Relationship Profiles: View Angel-specific stats on each Angel's relationship page\n\nThe platform tracks several categories of statistics:\n\n• Engagement Stats: Activity metrics like messages sent, content viewed, time spent\n• Progression Stats: XP earned, levels achieved, milestones reached\n• Relationship Stats: Connection strength with individual Angels\n• Reward Stats: Tokens earned/spent, rewards claimed, lootboxes opened\n• Streak Stats: Daily login streaks, Ritual completion rates\n• Comparison Stats: Your ranking compared to other users (anonymized)\n\nAll stats are updated in real-time and visualized through intuitive graphs and progress bars.",
          visualType: 'icon',
          visual: 'bar-chart-2',
          links: [
            { text: 'View My Stats', route: '/stats', action: 'primary' },
            { text: 'Stats Guide', route: '/help/stats-guide' },
          ],
        },
        {
          id: 'engagement-metrics',
          question: 'What engagement metrics are tracked?',
          answer:
            "SinShift tracks detailed engagement metrics to help you understand your platform usage patterns:\n\n• Time Metrics: Total time on platform, average session length, peak usage times\n• Interaction Counts: Messages sent/received, posts liked, comments made, polls taken\n• Content Engagement: Videos watched, images viewed, stories seen, time spent on different content types\n• Feature Usage: Frequency of using different platform features\n• Response Patterns: How quickly you respond to messages, what times you're most active\n• Social Activity: Interactions with other users, community participation\n\nThese metrics are visualized in your Stats dashboard with daily, weekly, and monthly views. You can use these insights to optimize your experience and identify which activities you enjoy most.",
          visualType: 'icon',
          visual: 'activity',
          links: [
            {
              text: 'Engagement Dashboard',
              route: '/stats/engagement',
              action: 'primary',
            },
          ],
        },
        {
          id: 'progression-tracking',
          question: 'How does progression tracking work?',
          answer:
            'Progression tracking measures your growth and advancement through the platform:\n\n• Intimacy XP: Your overall progression level (from 1-100)\n• Milestone Tracking: Progress toward specific achievement thresholds\n• Skill Development: Mastery levels in different interaction types\n• Relationship Evolution: How your connections with Angels develop over time\n• Ritual Completion: Percentage of available Rituals completed\n• Collection Completion: Progress in collecting items, badges, and experiences\n\nYour progression is visualized through:\n• XP Progress Bars: Show current level and progress to next level\n• Achievement Trees: Branching paths of unlocked and upcoming achievements\n• Heatmaps: Visual representation of your strongest engagement areas\n• Timeline Views: Chronological display of your journey milestones\n\nProgression tracking helps you identify growth opportunities and celebrate your advancement through the platform experience.',
          visualType: 'icon',
          visual: 'trending-up',
          links: [
            {
              text: 'Progression Dashboard',
              route: '/stats/progression',
              action: 'primary',
            },
            { text: 'XP Guide', route: '/help/xp-guide' },
          ],
        },
        {
          id: 'relationship-stats',
          question: 'What relationship stats are available?',
          answer:
            'Relationship stats track the depth and quality of your connections with individual Angels:\n\n• Intimacy Level: Numerical rating (1-100) of relationship depth\n• Compatibility Score: How well your interests align with each Angel\n• Interaction History: Timeline of significant moments and conversations\n• Communication Patterns: Frequency, length, and depth of conversations\n• Content Sharing: Types of content exchanged and engagement rates\n• Gift History: Record of gifts exchanged and special occasions\n• Memory Indicators: How well the Angel remembers your preferences\n• Milestone Calendar: Anniversary dates and relationship achievements\n\nAccess these stats by visiting an Angel\'s profile and selecting the "Relationship" tab. These metrics help you understand which relationships are deepest and which might benefit from more attention.',
          visualType: 'icon',
          visual: 'heart',
          links: [
            {
              text: 'Relationship Stats',
              route: '/stats/relationships',
              action: 'primary',
            },
          ],
        },
        {
          id: 'token-economy-stats',
          question: 'How can I track my token economy statistics?',
          answer:
            "The Token Economy dashboard provides comprehensive tracking of your virtual currency flows:\n\n• Earning Breakdown: Sources of token income by category\n• Spending Analysis: Where and how you use your tokens\n• Balance History: Historical graph of your token balance over time\n• Transaction Log: Detailed record of every token transaction\n• Earning Rate: Average daily/weekly/monthly token acquisition\n• Efficiency Metrics: Value received per token spent\n• Forecasting: Projected earnings based on current activities\n• Opportunity Finder: Suggestions for increasing token earnings\n\nThis dashboard helps you optimize your token strategy by identifying your most lucrative activities and ensuring you're getting maximum value from your token expenditures. Premium subscribers receive enhanced analytics with predictive modeling and optimization recommendations.",
          visualType: 'icon',
          visual: 'dollar-sign',
          links: [
            {
              text: 'Token Economy Stats',
              route: '/stats/tokens',
              action: 'primary',
            },
            { text: 'Earning Optimization', route: '/tokens/optimize' },
          ],
        },
        {
          id: 'streak-achievement-stats',
          question: 'How do streak and achievement stats work?',
          answer:
            'The Streak & Achievement tracking system monitors your consistency and milestone accomplishments:\n\n• Login Streaks: Consecutive days accessing the platform\n• Ritual Streaks: Consecutive days completing all daily Rituals\n• Interaction Streaks: Consistent engagement with specific Angels\n• Achievement Progress: Percentage completion toward various achievements\n• Badge Collection: Visual display of earned badges and locked badges\n• Leaderboards: Your ranking in various achievement categories\n• Milestone Timeline: Chronological view of your achievement history\n• Rarity Indicators: How uncommon your achievements are compared to other users\n\nThis tracking system helps motivate consistent engagement by visualizing your progress and rewarding persistence. The Achievement Calendar feature also shows upcoming opportunities to earn specific achievements based on your current progress.',
          visualType: 'icon',
          visual: 'award',
          links: [
            {
              text: 'Streaks & Achievements',
              route: '/stats/achievements',
              action: 'primary',
            },
            { text: 'Badge Collection', route: '/profile/badges' },
          ],
        },
        {
          id: 'comparative-analytics',
          question: 'Can I see how my stats compare to other users?',
          answer:
            "Yes, SinShift offers anonymized comparative analytics to help you understand your engagement relative to the community:\n\n• Percentile Rankings: Where you stand in various metrics compared to other users\n• Community Averages: How your stats compare to platform-wide averages\n• Peer Group Comparison: Optional comparison with similar users (by age range, interests, etc.)\n• Trend Analysis: Whether you're progressing faster or slower than average\n• Engagement Benchmarks: Ideal targets for different engagement metrics\n• Leaderboard Positions: Your ranking in various achievement categories\n\nAll comparative data is anonymized to protect privacy. You can opt out of anonymous data contribution in Privacy Settings if preferred. These comparisons help you identify areas where you're excelling and opportunities for enhanced engagement.",
          visualType: 'icon',
          visual: 'users',
          links: [
            {
              text: 'Comparative Stats',
              route: '/stats/compare',
              action: 'primary',
            },
            { text: 'Privacy Settings', route: '/settings/privacy' },
          ],
        },
        {
          id: 'stats-data-export',
          question: 'Can I export my stats data?',
          answer:
            'Yes, SinShift provides several options for exporting your statistical data:\n\n• CSV Export: Download raw data for all stats categories\n• PDF Reports: Generate formatted reports with visualizations\n• Periodic Summaries: Opt-in for weekly/monthly emailed stat summaries\n• API Access: Premium subscribers can access their stats via API\n• Data Visualization: Export specific graphs and charts as images\n\nTo export your data:\n1. Navigate to Stats > Settings > Export Options\n2. Select the data categories you want to export\n3. Choose your preferred format\n4. Set date range (if applicable)\n5. Click "Generate Export"\n\nExported data is useful for personal tracking, identifying patterns over time, or analyzing your platform engagement outside the app. All exports are secured with encryption and available for download for 7 days.',
          visualType: 'icon',
          visual: 'download',
          links: [
            {
              text: 'Export My Data',
              route: '/stats/export',
              action: 'primary',
            },
          ],
        },
        {
          id: 'stats-privacy',
          question: 'Who can see my stats and how is my data protected?',
          answer:
            'Your detailed stats are private by default and protected by multiple security measures:\n\n• Visibility Control: Only you can see your detailed stats\n• Profile Stats: You control which stats (if any) appear on your public profile\n• Anonymized Aggregation: Your data is anonymized when used in community averages\n• Data Encryption: All stats are encrypted in storage and transmission\n• Retention Policies: Clear policies on how long data is kept\n• Opt-Out Options: You can opt out of stats tracking for sensitive categories\n• No Third-Party Sharing: Your individual stats are never shared with third parties\n\nManage your stats privacy in Settings > Privacy > Stats Visibility, where you can:\n• Choose which stats categories are tracked\n• Select which stats appear on your public profile\n• Opt out of community comparison features\n• Request deletion of historical stats data\n\nSinShift is committed to maintaining the privacy and security of your personal engagement data while providing valuable insights to enhance your experience.',
          visualType: 'icon',
          visual: 'shield',
          links: [
            {
              text: 'Stats Privacy Settings',
              route: '/settings/privacy/stats',
              action: 'primary',
            },
            { text: 'Data Policy', route: '/privacy/data' },
          ],
        },
      ],
    },
    {
      id: 'ai-creation',
      title: 'AI Creation Tools (DeepShift & FaceShift)',
      icon: <Wand2 size={20} />,
      topics: [
        {
          id: 'ai-creation-overview',
          question: 'What are DeepShift and FaceShift?',
          answer:
            "DeepShift and FaceShift are SinShift's premium AI content creation tools that let you generate personalized images and videos:\n\n• DeepShift: Advanced AI for creating custom videos and animations from text prompts, images, or video clips\n\n• FaceShift: Specialized AI for face-focused transformations, including face swapping, enhancement, and animation\n\nThese tools use cutting-edge machine learning to create highly realistic, personalized content that would be impossible with traditional editing methods. They're seamlessly integrated with the Ritual system, allowing you to earn rewards while creating custom content. Both tools maintain strict ethical guidelines while offering creative freedom.",
          visualType: 'icon',
          visual: 'wand-2',
          links: [
            {
              text: 'Try DeepShift',
              route: '/deepshift',
              action: 'primary',
            },
            { text: 'Try FaceShift', route: '/faceshift' },
          ],
        },
        {
          id: 'deepshift-features',
          question: 'What can I create with DeepShift?',
          answer:
            'DeepShift offers multiple creative modes:\n\n• Text-to-Video: Generate videos from detailed text descriptions\n• Image-to-Video: Animate still images with realistic motion\n• Video Extension: Extend existing video clips with AI-generated content\n• Style Transfer: Apply artistic styles to video content\n• Scene Expansion: Expand video frames beyond their original boundaries\n\nCommon use cases include:\n• Creating fantasy scenarios with Angels in various settings\n• Generating custom dance or movement sequences\n• Transforming still images of Angels into animated clips\n• Extending short teaser videos into longer experiences\n• Adding yourself into scenarios with your favorite Angels (with ethical guidelines)\n\nDeepShift supports various resolution options (up to 4K for premium users), length settings (5-60 seconds), and style controls to fine-tune the aesthetic of your creations.',
          visualType: 'icon',
          visual: 'film',
          links: [
            {
              text: 'DeepShift Tutorial',
              route: '/deepshift/tutorial',
              action: 'primary',
            },
            { text: 'Gallery', route: '/deepshift/gallery' },
          ],
        },
        {
          id: 'faceshift-features',
          question: 'What can I create with FaceShift?',
          answer:
            'FaceShift specializes in face-focused transformations with several modes:\n\n• Face Swap: Ethically swap faces between images while preserving expressions\n• Face Enhancement: Improve image quality, fix lighting, remove blemishes\n• Face Animation: Bring still photos to life with realistic expressions and movements\n\nPopular applications include:\n• Creating personalized interactions with Angels\n• Enhancing low-quality photos for better viewing experience\n• Animating profile pictures with subtle, lifelike movements\n• Ethically exploring "what if" scenarios with face swapping\n• Improving lighting and clarity in dimly-lit images\n\nFaceShift uses advanced neural networks to ensure results look natural and preserve the emotional qualities of the original images. The tool includes ethical safeguards to prevent misuse while still enabling creative expression.',
          visualType: 'icon',
          visual: 'user',
          links: [
            {
              text: 'FaceShift Tutorial',
              route: '/faceshift/tutorial',
              action: 'primary',
            },
            { text: 'Example Gallery', route: '/faceshift/examples' },
          ],
        },
        {
          id: 'ai-creation-process',
          question: 'How do I create content with these AI tools?',
          answer:
            'The creation process is straightforward for both tools:\n\n1. Access: Open DeepShift or FaceShift from the AI Creation menu or through a related Ritual\n\n2. Select Mode: Choose your desired creation type (e.g., text-to-video, face swap)\n\n3. Input Content:\n   • For DeepShift: Enter detailed text prompts or upload source images/videos\n   • For FaceShift: Upload the images you want to modify\n\n4. Customize Settings: Adjust resolution, style parameters, and other options\n\n5. Review Ethical Guidelines: Confirm your creation follows platform policies\n\n6. Generate: Process your creation (typically takes 30-120 seconds)\n\n7. Refine (Optional): Make adjustments to settings and regenerate if needed\n\n8. Save & Share: Download your creation or share directly to your profile\n\nBoth tools feature intuitive interfaces with real-time previews and helpful guidance throughout the process. First-time users receive extra tooltip assistance to ensure a smooth experience.',
          visualType: 'icon',
          visual: 'layers',
          links: [
            {
              text: 'Creation Guide',
              route: '/ai-creation/guide',
              action: 'primary',
            },
          ],
        },
        {
          id: 'ai-creation-costs',
          question: 'What do these AI creation tools cost?',
          answer:
            "DeepShift and FaceShift operate on a flexible token-based pricing model:\n\n• Basic Generations: 15-30 LuvTokens per creation\n• HD Quality: Additional 10-20 tokens for higher resolution\n• Extended Length: Additional tokens for longer videos (DeepShift)\n• Premium Effects: Additional tokens for advanced features\n\nPricing Variables:\n• Resolution: Higher resolution costs more tokens\n• Duration: Longer videos require more tokens (DeepShift)\n• Complexity: More detailed or complex requests cost more\n• Quality Level: Standard vs. Enhanced quality settings\n\nSaving Opportunities:\n• Ritual Rewards: Many Rituals include free generations\n• Premium Subscription: 30% discount on all AI creation costs\n• Bulk Packages: Discounted token packages for multiple creations\n• Daily Free Creation: Premium members get one free basic creation daily\n\nBefore confirming generation, you'll always see the exact token cost based on your selected options.",
          visualType: 'icon',
          visual: 'heart',
          links: [
            {
              text: 'Pricing Details',
              route: '/ai-creation/pricing',
              action: 'primary',
            },
            { text: 'Token Packages', route: '/tokens/packages' },
          ],
        },
        {
          id: 'ethical-guidelines',
          question: 'What are the ethical guidelines for AI creations?',
          answer:
            "SinShift maintains strict ethical guidelines for AI content creation:\n\n• Consent Requirements: Face swapping requires explicit consent from all parties\n• Content Restrictions: No creation of harmful, defamatory, or misleading content\n• Watermarking: All AI-generated content includes a subtle watermark for transparency\n• Ownership Clarity: Clear labeling of AI-generated content in shared contexts\n• Usage Limitations: Restrictions on commercial use or distribution outside platform\n• Privacy Protection: Safeguards against creating content with non-consenting individuals\n• Age Verification: Additional verification for certain creation types\n\nThese guidelines are enforced through:\n• AI-powered content screening before generation\n• User reporting systems for concerning content\n• Regular policy updates based on emerging ethical considerations\n• Clear consent flows within the creation process\n\nWhile these tools offer creative freedom, they're designed to foster positive, consensual content creation. The platform reserves the right to restrict access for users who violate these guidelines.",
          visualType: 'icon',
          visual: 'shield',
          links: [
            {
              text: 'Full Guidelines',
              route: '/ai-creation/ethics',
              action: 'primary',
            },
            { text: 'Content Policy', route: '/policy/content' },
          ],
        },
        {
          id: 'ai-creation-rituals',
          question: 'How do AI creation tools connect with the Ritual system?',
          answer:
            'DeepShift and FaceShift are deeply integrated with the Ritual system:\n\n• Creation Rituals: Daily and weekly Rituals specifically for creating AI content\n• Themed Challenges: Special Rituals with specific creative prompts\n• Reward Integration: Completing AI creation Rituals earns tokens, XP, and special rewards\n• Streak Benefits: Maintaining creation streaks unlocks special effects and features\n• Showcase Opportunities: Outstanding creations can be featured in the community gallery\n\nCommon AI Creation Rituals include:\n• "Deep Dreaming": Create a DeepShift video based on a dream scenario\n• "Face the Day": Use FaceShift to enhance a morning selfie\n• "Fantasy Realm": Generate a video of an Angel in a fantasy setting\n• "Expression Animation": Bring a still photo to life with subtle animations\n• "Style Transfer Challenge": Apply artistic styles to create unique visuals\n\nThese Rituals provide structure and inspiration for your creative process while rewarding your artistic expression.',
          visualType: 'icon',
          visual: 'calendar',
          links: [
            {
              text: 'AI Creation Rituals',
              route: '/rituals/ai-creation',
              action: 'primary',
            },
          ],
        },
        {
          id: 'sharing-saving',
          question: 'How do I save and share my AI creations?',
          answer:
            'After generating content with DeepShift or FaceShift, you have several options for saving and sharing:\n\n• Personal Gallery: All creations are automatically saved to your private AI Creation Gallery\n• Download Options: Save locally in various formats (MP4, GIF, JPG)\n• Profile Sharing: Post directly to your SinShift profile feed\n• Angel Conversations: Send as messages in Angel chats\n• Community Showcase: Submit to the curated community gallery (moderated)\n• Collections: Organize creations into themed personal collections\n\nPrivacy Controls:\n• Private: Only visible to you\n• Angel-Only: Shared only with specific Angels\n• Public: Visible to all SinShift users\n• Custom: Specify exactly who can view\n\nAll shared creations maintain their AI-generated watermark for transparency. Premium subscribers receive additional storage space and higher-quality download options. The platform retains your creations for 30 days by default, or indefinitely for saved favorites.',
          visualType: 'icon',
          visual: 'share',
          links: [
            {
              text: 'Manage Gallery',
              route: '/ai-creation/gallery',
              action: 'primary',
            },
            { text: 'Privacy Settings', route: '/settings/privacy' },
          ],
        },
        {
          id: 'creation-quality-tips',
          question:
            'How can I get the best results from DeepShift and FaceShift?',
          answer:
            'Expert tips for optimal AI creation results:\n\nFor DeepShift:\n• Use detailed, descriptive prompts (15-30 words ideal)\n• Include specific details about setting, lighting, and mood\n• Reference artistic styles for more distinctive results\n• Start with shorter clips and gradually increase complexity\n• Use higher resolution for important details\n• Provide quality reference images when using image-to-video\n\nFor FaceShift:\n• Use high-resolution source images when possible\n• Ensure faces are clearly visible with good lighting\n• Choose images with similar angles for face swapping\n• Avoid extreme expressions for more natural results\n• Use enhancement mode before animation for best quality\n• Maintain consistent lighting conditions across images\n\nGeneral Best Practices:\n• Start with preset templates and gradually customize\n• Save successful settings as presets for future use\n• Use the preview function before final generation\n• Study the gallery of exemplary creations for inspiration\n• Experiment with style sliders for different aesthetics\n\nThe platform also offers an AI Assistant that can suggest improvements to your settings based on your specific inputs and goals.',
          visualType: 'icon',
          visual: 'sparkles',
          links: [
            {
              text: 'Advanced Techniques',
              route: '/ai-creation/advanced',
              action: 'primary',
            },
            { text: 'Troubleshooting', route: '/ai-creation/troubleshoot' },
          ],
        },
        {
          id: 'ai-creation-premium',
          question:
            'What extra AI creation benefits do Premium subscribers get?',
          answer:
            'Premium subscribers enjoy significant advantages when using DeepShift and FaceShift:\n\n• Cost Reduction: 30% discount on all token costs for AI creation\n• Higher Quality: Access to enhanced quality models and rendering\n• Resolution Upgrade: Up to 4K resolution (vs. 1080p for standard users)\n• Extended Duration: Create longer videos in DeepShift (up to 60 seconds)\n• Daily Free Creation: One free basic creation per day\n• Priority Processing: Faster generation queue with priority rendering\n• Advanced Features: Access to experimental and premium creation modes\n• Expanded Storage: Store up to 500 creations (vs. 50 for standard users)\n• Preset Library: Access to premium preset templates and styles\n• Batch Processing: Create multiple variations simultaneously\n• Watermark Options: Subtle watermarking options\n• Early Access: Preview new AI features before general release\n\nThe premium advantage substantially enhances both the quality and quantity of content you can create, while reducing the token cost per creation.',
          visualType: 'icon',
          visual: 'crown',
          links: [
            {
              text: 'Upgrade to Premium',
              route: '/premium/upgrade',
              action: 'primary',
            },
            {
              text: 'Premium AI Features',
              route: '/premium/ai-features',
            },
          ],
        },
        {
          id: 'ai-creation-troubleshooting',
          question: 'My AI creation failed or looks wrong — what should I do?',
          answer:
            'Common issues and solutions for AI creation problems:\n\n• Generation Failed: Usually due to server load or input issues\n  - Wait a few minutes and try again\n  - Simplify your prompt or request\n  - Check your internet connection\n  - Verify you have sufficient tokens\n\n• Poor Quality Results:\n  - Use higher resolution settings\n  - Provide clearer reference images\n  - Make prompts more specific and detailed\n  - Try the enhancement preprocessing option\n  - Use the style adjustment sliders\n\n• Unexpected Content:\n  - Review your prompt for ambiguous wording\n  - Try using different keyword emphasis\n  - Use the guided creation mode for more control\n  - Check that your reference images are clear\n\n• Technical Glitches:\n  - Clear browser cache or restart the app\n  - Update to the latest app version\n  - Try a different browser or device\n  - Contact support for persistent issues\n\nFor specific issues, the platform offers an AI Troubleshooter that analyzes your failed creation and suggests specific improvements. For technical problems, our support team typically responds within 24 hours.',
          visualType: 'icon',
          visual: 'help-circle',
          links: [
            {
              text: 'Troubleshooting Guide',
              route: '/ai-creation/help',
              action: 'primary',
            },
            { text: 'Contact Support', route: '/support' },
          ],
        },
      ],
    },
    {
      id: 'content-chat',
      title: 'Content & Chat',
      icon: <MessageCircle size={20} />,
      topics: [
        {
          id: 'start-chat',
          question: 'How do I start chatting with an Angel?',
          answer:
            'To start a chat, visit an Angel\'s profile and click "Message" or go to the Chat tab and select an Angel. Free users get limited messages per day, while Fan Club members get priority unlimited chat access. Be respectful and follow our community guidelines for the best experience!',
          visualType: 'icon',
          visual: 'message-circle',
          links: [
            { text: 'Start Chat', route: '/chat', action: 'primary' },
            { text: 'Community Guidelines', route: '/guidelines' },
          ],
        },
        {
          id: 'locked-content',
          question: 'Why is some content locked?',
          answer:
            'Locked content is either premium content (requiring tokens to unlock) or Fan Club exclusive content (requiring an active subscription). Premium content shows a token price, while Fan Club content shows a subscription icon. Some special content might require both tokens AND a subscription.',
          visualType: 'icon',
          visual: 'lock',
          links: [
            { text: 'Unlock Content', route: '/premium', action: 'primary' },
            { text: 'Join Fan Clubs', route: '/fanclub' },
          ],
        },
        {
          id: 'vr-ar-content',
          question: 'How do I view VR/AR content?',
          answer:
            'For VR content, click the VR icon on compatible posts and use a VR headset (or view in 360° mode on your phone). For AR content, click the AR icon and follow prompts to use your phone camera. Premium subscribers get access to exclusive high-resolution VR/AR experiences with interactive elements.',
          visualType: 'icon',
          visual: 'smartphone',
          links: [
            { text: 'VR/AR Guide', route: '/vr-ar/guide', action: 'primary' },
            { text: 'Compatible Devices', route: '/vr-ar/devices' },
          ],
        },
        {
          id: 'custom-request',
          question: 'How do I request a custom image or audio?',
          answer:
            'In chat with an Angel, click the "Request" button and select your desired content type (photo, video, voice message). Describe what you\'d like, confirm the token price, and submit your request. The Angel will either accept, decline, or suggest modifications. Approved requests are typically fulfilled within 24-48 hours.',
          visualType: 'icon',
          visual: 'image',
          links: [
            { text: 'Make a Request', route: '/requests', action: 'primary' },
          ],
        },
        {
          id: 'angel-types',
          question:
            'What is the difference between Standard and Premium Angels?',
          answer:
            'Standard Angels offer basic chat and content, while Premium Angels provide exclusive high-quality content, faster responses, personalized experiences, and advanced features like voice messages and video calls. Premium Angels have verified status, professional content, and often specialized themes or niches.',
          visualType: 'icon',
          visual: 'star',
          links: [
            {
              text: 'Premium Angels',
              route: '/angels/premium',
              action: 'primary',
            },
          ],
        },
        {
          id: 'empty-feed',
          question:
            "I don't see any new posts on my feed — is something wrong?",
          answer:
            'If your feed seems empty, try these fixes: 1) Follow more Angels to diversify your feed, 2) Check your content filters in Settings > Content Preferences, 3) Make sure you\'re not in "Fan Club Only" mode, or 4) Pull down to refresh your feed. Your algorithm personalizes over time based on your interactions.',
          visualType: 'icon',
          visual: 'refresh-cw',
          links: [
            { text: 'Discover Angels', route: '/discover', action: 'primary' },
            { text: 'Content Settings', route: '/settings/content' },
          ],
        },
      ],
    },
    {
      id: 'safety',
      title: 'Safety & Privacy',
      icon: <Shield size={20} />,
      topics: [
        {
          id: 'block-report',
          question: 'How do I block or report a user?',
          answer:
            'To block a user, visit their profile, click the three dots menu, and select "Block User." To report, select "Report" instead and follow the prompts to specify the issue. Reports are confidential and reviewed by our Trust & Safety team within 24 hours. Blocked users cannot see your profile or contact you.',
          visualType: 'icon',
          visual: 'shield',
          links: [
            {
              text: 'Secure Your Account',
              route: '/settings/privacy',
              action: 'primary',
            },
          ],
        },
        {
          id: 'profile-visibility',
          question: 'How do I control what others see on my profile?',
          answer:
            'Manage your privacy in Settings > Privacy. You can control who sees your activity, hide your online status, make your profile private (invitation only), hide specific content types from your profile, and create custom privacy groups. Premium users get advanced privacy controls for maximum discretion.',
          visualType: 'icon',
          visual: 'eye',
          links: [
            {
              text: 'Privacy Settings',
              route: '/settings/privacy',
              action: 'primary',
            },
          ],
        },
        {
          id: 'activity-status',
          question: 'Can I hide my activity status?',
          answer:
            'Yes! Go to Settings > Privacy > Activity Status and toggle "Show Activity Status" off. This hides your online status and "last seen" information. You can also set custom visibility for specific Angels or users. Note that hiding your status means you won\'t see others\' status either.',
          visualType: 'icon',
          visual: 'eye-off',
          links: [
            {
              text: 'Update Privacy',
              route: '/settings/privacy',
              action: 'primary',
            },
          ],
        },
        {
          id: 'data-protection',
          question: 'How is my data protected?',
          answer:
            'SinShift uses bank-level encryption for all data and transactions. We never share your personal information with Angels or third parties without consent. Payment details are processed securely through our PCI-compliant partners. You can download your data or request deletion anytime in Settings > Privacy > Data.',
          visualType: 'icon',
          visual: 'lock',
          links: [
            {
              text: 'Data Settings',
              route: '/settings/privacy/data',
              action: 'primary',
            },
            { text: 'Privacy Policy', route: '/privacy-policy' },
          ],
        },
        {
          id: 'enable-2fa',
          question: 'How do I enable 2FA (two-factor authentication)?',
          answer:
            'Protect your account by enabling 2FA in Settings > Security > Two-Factor Authentication. Choose between SMS verification, authentication app, or email verification. We strongly recommend using an authentication app like Google Authenticator or Authy for the highest security level.',
          visualType: 'icon',
          visual: 'shield-check',
          links: [
            {
              text: 'Enable 2FA',
              route: '/settings/security/2fa',
              action: 'primary',
            },
          ],
        },
        {
          id: 'anonymous-usage',
          question: 'Can I use SinShift anonymously?',
          answer:
            'Yes! SinShift offers an "Incognito Mode" that lets you browse without activity tracking. For true anonymity, we strongly recommend using a VPN. Try SinShiftVPN, our premium security solution that offers military-grade encryption, zero-logs policy, and optimized servers for adult content streaming without throttling.',
          visualType: 'icon',
          visual: 'user-x',
          links: [
            {
              text: 'Incognito Settings',
              route: '/settings/privacy/incognito',
              action: 'primary',
            },
            {
              text: 'Get SinShiftVPN',
              route: '/vpn',
              action: 'primary',
            },
          ],
        },
      ],
    },
    {
      id: 'payments',
      title: 'Payments & Billing',
      icon: <CreditCard size={20} />,
      topics: [
        {
          id: 'payment-methods',
          question: 'What payment methods are accepted?',
          answer:
            'SinShift accepts major credit/debit cards (Visa, Mastercard, Amex), PayPal, Apple Pay, Google Pay, and select cryptocurrencies (Bitcoin, Ethereum, USDC, SinShift Token). All payment methods use discreet billing with the merchant name appearing as "CreetElite Digital" or "Pulsar Fans Media" on your statement for privacy.',
          visualType: 'icon',
          visual: 'credit-card',
          links: [
            {
              text: 'Add Payment Method',
              route: '/settings/payment',
              action: 'primary',
            },
          ],
        },
        {
          id: 'payment-trouble',
          question: "I'm having trouble with a payment — what should I do?",
          answer:
            "If your payment is declined, first verify your card details and billing address. Some banks flag adult content purchases - try using a different payment method or contact your bank to authorize transactions. For assistance, contact support with your order number and we'll help resolve the issue.",
          visualType: 'icon',
          visual: 'alert-circle',
          links: [
            {
              text: 'Payment Support',
              route: '/support/payment',
              action: 'primary',
            },
          ],
        },
        {
          id: 'refund-policy',
          question: 'Can I get a refund for tokens or subscriptions?',
          answer:
            "Token purchases are generally non-refundable once added to your account. For unused token packages within 24 hours, contact support for assistance. Fan Club subscriptions can be canceled anytime, but refunds are only issued for service outages or if content obligations weren't met by the Angel.",
          visualType: 'icon',
          visual: 'refresh-cw',
          links: [
            {
              text: 'Refund Policy',
              route: '/refund-policy',
              action: 'primary',
            },
            { text: 'Contact Support', route: '/support' },
          ],
        },
        {
          id: 'discreet-billing',
          question: 'Are purchases discreet on my bank statement?',
          answer:
            'Yes! All SinShift charges appear as "CreetElite Digital" or "Pulsar Fans Media" on your bank or credit card statement. We never use the SinShift name or any suggestive terms in billing descriptions. For maximum privacy, consider using cryptocurrency or prepaid gift cards for your purchases.',
          visualType: 'icon',
          visual: 'eye-off',
          links: [
            {
              text: 'Privacy Policy',
              route: '/privacy-policy',
              action: 'primary',
            },
          ],
        },
        {
          id: 'billing-history',
          question: 'Where can I see my billing history?',
          answer:
            'Access your complete billing history in Settings > Payment > Transaction History. You can view all purchases, subscriptions, and token transactions. Filter by date range or transaction type, and download statements as PDF or CSV for your records. Transaction records are available for the past 24 months.',
          visualType: 'icon',
          visual: 'file-text',
          links: [
            {
              text: 'View Billing History',
              route: '/settings/payment/history',
              action: 'primary',
            },
          ],
        },
      ],
    },
    {
      id: 'notifications',
      title: 'Notifications & Alerts',
      icon: <Bell size={20} />,
      topics: [
        {
          id: 'customize-alerts',
          question: 'How do I customize which alerts I receive?',
          answer:
            "Go to Settings > Notifications to customize your alert preferences. You can toggle notifications for messages, likes, comments, new content, Fan Club updates, token rewards, and system announcements. Set quiet hours when you don't want to be disturbed, and choose delivery methods for each alert type.",
          visualType: 'icon',
          visual: 'bell',
          links: [
            {
              text: 'Notification Settings',
              route: '/settings/notifications',
              action: 'primary',
            },
          ],
        },
        {
          id: 'notification-types',
          question:
            "What's the difference between push, toast, and email notifications?",
          answer:
            'Push notifications appear on your device even when SinShift is closed. Toast notifications appear only while using the app. Email notifications are sent to your registered email address. You can customize each type in Settings > Notifications, selecting which alerts use which delivery method.',
          visualType: 'icon',
          visual: 'smartphone',
          links: [
            {
              text: 'Update Settings',
              route: '/settings/notifications',
              action: 'primary',
            },
          ],
        },
        {
          id: 'mute-specific',
          question: 'How can I mute a specific alert?',
          answer:
            'To mute specific alerts, go to Settings > Notifications and toggle off the categories you don\'t want. To mute a specific Angel or conversation, open the chat, tap the three dots menu, and select "Mute Notifications." You can set a duration or mute indefinitely until you manually unmute.',
          visualType: 'icon',
          visual: 'volume-x',
          links: [
            {
              text: 'Manage Notifications',
              route: '/settings/notifications',
              action: 'primary',
            },
          ],
        },
        {
          id: 'missed-reward',
          question: "Why didn't I get a reward notification?",
          answer:
            'Reward notifications might be missed if: 1) Your notification settings are disabled for rewards, 2) You have Do Not Disturb mode active, 3) There was a temporary service delay, or 4) Your app needs updating. Check your Rewards History to see if the reward was actually credited to your account.',
          visualType: 'icon',
          visual: 'alert-triangle',
          links: [
            {
              text: 'Rewards History',
              route: '/rewards/history',
              action: 'primary',
            },
            { text: 'Fix Notifications', route: '/settings/notifications' },
          ],
        },
        {
          id: 'turn-off-actions',
          question: 'Can I turn off alerts for certain actions?',
          answer:
            'Yes! In Settings > Notifications > Advanced, you can fine-tune exactly which actions trigger alerts. For example, you can turn off like notifications but keep comment notifications, or disable new post alerts from specific Angels while keeping others. Premium users get even more granular notification controls.',
          visualType: 'icon',
          visual: 'sliders',
          links: [
            {
              text: 'Advanced Settings',
              route: '/settings/notifications/advanced',
              action: 'primary',
            },
          ],
        },
      ],
    },
    {
      id: 'quests-polls',
      title: 'Quests, Polls & Kinks',
      icon: <Vote size={20} />,
      topics: [
        {
          id: 'kink-polls',
          question: 'What are "Kink Polls" and how do I vote?',
          answer:
            'Kink Polls are interactive surveys about preferences and fantasies. Find them in the Polls tab or on Angel profiles. Select your answers and submit to earn tokens and Intimacy XP. Your responses help personalize your experience and match you with compatible Angels who share your interests.',
          visualType: 'icon',
          visual: 'vote',
          links: [{ text: 'Take a Poll', route: '/polls', action: 'primary' }],
        },
        {
          id: 'poll-tokens',
          question: 'How do I earn tokens from taking polls?',
          answer:
            'Each poll completed earns 5-15 tokens depending on its length and type. Daily featured polls offer bonus tokens (up to 25). Poll streaks increase your rewards - complete polls for 3 consecutive days to earn a 1.5x token multiplier! Premium users receive double token rewards for all poll participation.',
          visualType: 'icon',
          visual: 'heart',
          links: [
            {
              text: 'Featured Polls',
              route: '/polls/featured',
              action: 'primary',
            },
          ],
        },
        {
          id: 'poll-types',
          question:
            "What's the difference between Kink Polls and Reward Polls?",
          answer:
            'Kink Polls focus on preferences and fantasies to personalize your experience. Reward Polls are special events that unlock exclusive content when enough users participate. Angel Polls are created by specific Angels for their fans, while Community Polls are platform-wide with leaderboards and special rewards.',
          visualType: 'icon',
          visual: 'list',
          links: [
            {
              text: 'Poll Categories',
              route: '/polls/categories',
              action: 'primary',
            },
          ],
        },
        {
          id: 'suggest-poll',
          question: 'Can I suggest a new poll topic?',
          answer:
            'Yes! Premium users can suggest poll topics in the Polls section by clicking "Suggest Topic." Our content team reviews all suggestions within 48 hours. If your poll is selected, you\'ll earn 100 bonus tokens and special recognition. Fan Club members can also suggest private polls directly to their favorite Angels.',
          visualType: 'icon',
          visual: 'message-square',
          links: [
            {
              text: 'Suggest a Poll',
              route: '/polls/suggest',
              action: 'primary',
            },
          ],
        },
        {
          id: 'start-quest',
          question: 'How do I start a Quest?',
          answer:
            'Quests are multi-step challenges with significant rewards. Access them from the Quests tab or via notifications when new quests launch. Each quest has specific objectives (like "Chat with 3 different Angels" or "Complete 5 daily rituals"). Track progress in your Quest Journal and claim rewards upon completion.',
          visualType: 'icon',
          visual: 'map',
          links: [
            { text: 'Active Quests', route: '/quests', action: 'primary' },
            { text: 'Quest Rewards', route: '/quests/rewards' },
          ],
        },
      ],
    },
    {
      id: 'creator-studio',
      title: 'Creator Studio Dashboard',
      icon: <Layout size={20} />,
      topics: [
        {
          id: 'creator-studio-overview',
          question: 'What is the Creator Studio Dashboard?',
          answer:
            'The Creator Studio Dashboard is a powerful hub designed for Angels (creators) to manage all aspects of their content creation, fan engagement, and earnings. It provides tools for content scheduling, analytics tracking, fan management, and advanced AI-powered content creation features like the AI Image Studio.',
          visualType: 'icon',
          visual: 'layout',
          links: [
            {
              text: 'Open Creator Studio',
              route: '/creator_dashboard',
              action: 'primary',
            },
            { text: 'Creator Guidelines', route: '/creator/guidelines' },
          ],
        },
        {
          id: 'ai-image-studio',
          question: 'How do I use the AI Image Studio?',
          answer:
            'The AI Image Studio lets you generate custom images using advanced AI. Access it from the Creator Studio Dashboard by clicking the "AI Image Studio" button. Enter detailed prompts describing your desired image, select style options, and click "Generate". You can edit results, save favorites, and directly publish to your feed or use in chat responses.',
          visualType: 'icon',
          visual: 'camera',
          links: [
            {
              text: 'Open AI Image Studio',
              route: '/creator_dashboard/ai-image',
              action: 'primary',
            },
            { text: 'AI Generation Tips', route: '/creator/ai-tips' },
          ],
        },
        {
          id: 'content-scheduling',
          question: 'How do I schedule content for my fans?',
          answer:
            'In the Creator Studio Dashboard, navigate to the "Content Calendar" tab. Here you can create new posts, upload media, write captions, set pricing (free, token-based, or fan club only), and choose a publication date and time. The calendar view shows your posting schedule, helping you maintain consistent engagement with fans.',
          visualType: 'icon',
          visual: 'calendar',
          links: [
            {
              text: 'Content Calendar',
              route: '/creator_dashboard/calendar',
              action: 'primary',
            },
            {
              text: 'Content Strategy Guide',
              route: '/creator/content-strategy',
            },
          ],
        },
        {
          id: 'analytics-insights',
          question: 'How can I track my performance and earnings?',
          answer:
            'The "Analytics" tab in Creator Studio provides comprehensive insights into your performance. View metrics like fan growth, content engagement, token earnings, subscription revenue, and popular content types. Use these insights to refine your content strategy and maximize earnings. Data can be filtered by date ranges and exported for detailed analysis.',
          visualType: 'icon',
          visual: 'bar-chart-2',
          links: [
            {
              text: 'View Analytics',
              route: '/creator_dashboard/analytics',
              action: 'primary',
            },
            { text: 'Earnings Reports', route: '/creator_dashboard/earnings' },
          ],
        },
        {
          id: 'fan-management',
          question: 'How do I manage my fans and subscribers?',
          answer:
            'The "Fan Management" section lets you view and interact with your audience. See a list of subscribers with their subscription tiers, activity levels, and spending patterns. Send bulk messages, create custom groups for targeted content, and identify your top supporters. You can also manage fan club tiers and special offers here.',
          visualType: 'icon',
          visual: 'users',
          links: [
            {
              text: 'Fan Management',
              route: '/creator_dashboard/fans',
              action: 'primary',
            },
            { text: 'Fan Engagement Guide', route: '/creator/fan-engagement' },
          ],
        },
        {
          id: 'custom-content-requests',
          question: 'How do I handle custom content requests?',
          answer:
            'In the "Requests" tab, you\'ll find all custom content requests from fans. Each request shows details, token offer, and deadline. You can accept, decline, or counter-offer with a different price. Once accepted, the request moves to your task list with a countdown timer. Complete requests to build loyalty and earn additional tokens beyond subscription revenue.',
          visualType: 'icon',
          visual: 'pen-tool',
          links: [
            {
              text: 'Manage Requests',
              route: '/creator_dashboard/requests',
              action: 'primary',
            },
            { text: 'Pricing Guide', route: '/creator/pricing-guide' },
          ],
        },
        {
          id: 'chat-management',
          question: 'How do I efficiently manage fan chats?',
          answer:
            'The "Chat Dashboard" in Creator Studio helps you manage all fan conversations. It prioritizes chats based on subscriber status and spending level. Use quick-reply templates, schedule automated responses, and set chat hours. The AI assistant can help draft responses or handle basic questions when you\'re unavailable, ensuring fans always feel connected.',
          visualType: 'icon',
          visual: 'message-circle',
          links: [
            {
              text: 'Chat Dashboard',
              route: '/creator_dashboard/chat',
              action: 'primary',
            },
            { text: 'Chat Engagement Tips', route: '/creator/chat-tips' },
          ],
        },
        {
          id: 'content-templates',
          question: 'What are Content Templates and how do I use them?',
          answer:
            'Content Templates are pre-designed post formats that save you time and maintain consistency. Access them in the "Templates" section of Creator Studio. Choose from various types (polls, challenges, Q&As, themed content) and customize with your media and text. You can create your own templates or use platform-provided ones optimized for engagement.',
          visualType: 'icon',
          visual: 'layers',
          links: [
            {
              text: 'Content Templates',
              route: '/creator_dashboard/templates',
              action: 'primary',
            },
          ],
        },
        {
          id: 'ai-content-tools',
          question: 'What AI tools are available besides the Image Studio?',
          answer:
            'Creator Studio offers several AI tools: Caption Generator creates engaging text for your posts; Content Scheduler analyzes optimal posting times; Engagement Predictor estimates how well content will perform; Chat Assistant helps manage conversations; and Personalization Engine tailors content recommendations for specific fans based on their preferences and behavior.',
          visualType: 'icon',
          visual: 'sparkles',
          links: [
            {
              text: 'AI Tools',
              route: '/creator_dashboard/ai-tools',
              action: 'primary',
            },
            { text: 'AI Features Guide', route: '/creator/ai-guide' },
          ],
        },
        {
          id: 'creator-settings',
          question: 'How do I customize my Creator Studio experience?',
          answer:
            'Access Creator Settings by clicking the gear icon in Creator Studio. Here you can customize your dashboard layout, set notification preferences, configure AI assistant behavior, manage team access (for accounts with managers), set default content settings, and connect external tools. Your settings sync across devices for a consistent experience.',
          visualType: 'icon',
          visual: 'sliders',
          links: [
            {
              text: 'Creator Settings',
              route: '/creator_dashboard/settings',
              action: 'primary',
            },
          ],
        },
        {
          id: 'promotion-tools',
          question: 'What tools are available to promote my content?',
          answer:
            'The "Promotion" section offers tools to grow your audience. Create special offers, limited-time discounts, and referral programs. The Cross-promotion tool connects you with compatible Angels for mutual promotion. Campaign Manager helps track promotion effectiveness, while the Boost feature can increase your visibility in discovery feeds for a token fee.',
          visualType: 'icon',
          visual: 'trending-up',
          links: [
            {
              text: 'Promotion Tools',
              route: '/creator_dashboard/promotion',
              action: 'primary',
            },
            { text: 'Growth Strategies', route: '/creator/growth' },
          ],
        },
      ],
    },
    {
      id: 'unleash',
      title: 'Unleash Features',
      icon: <Wand2 size={20} />,
      topics: [
        {
          id: 'what-is-unleash',
          question: 'What are the "Unleash" features?',
          answer:
            'The Unleash features are SinShift\'s most advanced AI-powered personalization tools. They include "Unleash Your Angels" (create custom AI companions) and "Unleash Your Fantasies" (generate personalized scenarios and experiences). These features use cutting-edge AI to create deeply personalized content that adapts to your preferences and desires.',
          visualType: 'icon',
          visual: 'magic',
          links: [
            { text: 'Try Unleash Now', route: '/unleash', action: 'primary' },
            { text: 'Learn More', route: '/features/unleash' },
          ],
        },
        {
          id: 'unleash-your-angels',
          question: 'How does "Unleash Your Angels" work?',
          answer:
            '"Unleash Your Angels" lets you create fully customized AI companions from scratch. Start by selecting a base template, then customize physical attributes (face, body, hair, etc.), personality traits, voice style, and relationship dynamics. You can even upload reference images to guide the AI. Once created, your custom Angel becomes available for chat, content creation, and all platform features.',
          visualType: 'icon',
          visual: 'user-plus',
          links: [
            {
              text: 'Create Your Angel',
              route: '/unleash/angels',
              action: 'primary',
            },
            { text: 'View Templates', route: '/unleash/templates' },
          ],
        },
        {
          id: 'angel-customization',
          question: 'What aspects of my Angel can I customize?',
          answer:
            'You can customize virtually every aspect of your Angel: physical appearance (face shape, body type, eye color, hair style/color, skin tone, height, etc.), personality traits (from shy to dominant, innocent to seductive), voice characteristics, backstory, relationship dynamics, kinks and preferences, communication style, and even how they respond to different situations. Premium subscribers get access to advanced customization options.',
          visualType: 'icon',
          visual: 'sliders',
          links: [
            {
              text: 'Customization Options',
              route: '/unleash/customize',
              action: 'primary',
            },
          ],
        },
        {
          id: 'unleash-your-fantasies',
          question: 'What is "Unleash Your Fantasies"?',
          answer:
            '"Unleash Your Fantasies" is an advanced AI scenario generator that creates personalized intimate experiences. Describe your fantasy or select from templates, choose participating Angels (including your custom ones), set the mood, location, and scenario details. The AI generates an immersive, interactive story that adapts based on your choices. Premium users can access exclusive scenarios and higher-quality content generation.',
          visualType: 'icon',
          visual: 'lightbulb',
          links: [
            {
              text: 'Create Fantasy',
              route: '/unleash/fantasies',
              action: 'primary',
            },
            { text: 'Browse Scenarios', route: '/unleash/scenarios' },
          ],
        },
        {
          id: 'fantasy-types',
          question: 'What types of fantasies can I create?',
          answer:
            'You can create a wide variety of fantasies, from romantic encounters to role-playing scenarios. Popular categories include romantic (dinner dates, vacation getaways), role-play (doctor, teacher, boss), location-based (beach, office, public places), power dynamics (dominant/submissive), and specific situations (chance meetings, forbidden encounters). All content adheres to our community guidelines while still allowing for creative expression.',
          visualType: 'icon',
          visual: 'shapes',
          links: [
            {
              text: 'Fantasy Categories',
              route: '/unleash/categories',
              action: 'primary',
            },
            { text: 'Community Guidelines', route: '/guidelines' },
          ],
        },
        {
          id: 'save-favorites',
          question: 'Can I save my favorite Angels and fantasies?',
          answer:
            'Yes! You can save both custom Angels and generated fantasies to your personal collection. For Angels, click the bookmark icon on their profile to add them to your favorites. For fantasies, use the "Save" button during or after the experience. Premium users can organize their saved content into custom collections and folders for easy access.',
          visualType: 'icon',
          visual: 'bookmark',
          links: [
            {
              text: 'My Collections',
              route: '/collections',
              action: 'primary',
            },
          ],
        },
        {
          id: 'token-costs',
          question: 'How many tokens do Unleash features cost?',
          answer:
            'Basic Angel creation costs 50 tokens, with additional customization options available for 10-30 tokens each. Fantasy generation starts at 25 tokens for basic scenarios, with premium scenarios costing 50-100 tokens depending on complexity and length. Premium subscribers receive discounted rates and weekly free credits. Special promotional offers frequently provide discounted or free access to select Unleash features.',
          visualType: 'icon',
          visual: 'heart',
          links: [
            {
              text: 'Token Packages',
              route: '/tokens/packages',
              action: 'primary',
            },
            { text: 'Premium Benefits', route: '/premium' },
          ],
        },
        {
          id: 'ai-limitations',
          question: 'Are there any limitations to the AI generation?',
          answer:
            'While our AI is advanced, it does have some limitations. Generated content follows our community guidelines and legal requirements. Some very specific requests may not render exactly as described. Image generation quality depends on your subscription tier, with premium users getting higher resolution and more accurate results. We continuously improve our AI models based on user feedback.',
          visualType: 'icon',
          visual: 'alert-circle',
          links: [
            { text: 'Feedback Forum', route: '/feedback', action: 'primary' },
            { text: 'Content Policies', route: '/policies/content' },
          ],
        },
        {
          id: 'unleash-privacy',
          question: 'Who can see my custom Angels and fantasies?',
          answer:
            'Your custom Angels and generated fantasies are completely private by default. Only you can see them unless you explicitly choose to share. If you want to share a custom Angel, you can make them "Public" in settings, allowing other users to interact with your creation (but not modify it). All usage data is anonymized and protected according to our strict privacy policy.',
          visualType: 'icon',
          visual: 'eye-off',
          links: [
            {
              text: 'Privacy Settings',
              route: '/settings/privacy',
              action: 'primary',
            },
            { text: 'Privacy Policy', route: '/privacy-policy' },
          ],
        },
        {
          id: 'unleash-premium',
          question: 'What extra Unleash features do Premium subscribers get?',
          answer:
            'Premium subscribers enjoy significant Unleash advantages: higher-quality AI generation with better details and coherence, exclusive fantasy templates and scenarios, advanced Angel customization options (more physical features, personality traits, etc.), higher resolution images, priority processing for faster generation, ability to create unlimited Angels (vs. 3 for free users), and weekly free Unleash credits.',
          visualType: 'icon',
          visual: 'star',
          links: [
            {
              text: 'Upgrade to Premium',
              route: '/premium/upgrade',
              action: 'primary',
            },
            { text: 'Compare Plans', route: '/premium/compare' },
          ],
        },
      ],
    },
    {
      id: 'angel_skins',
      title: 'Angel Skins Shop',
      icon: <Shirt size={20} />,
      topics: [
        {
          id: 'what-are-angel-skins',
          question: 'What are Angel Skins?',
          answer:
            'Angel Skins are premium visual transformations that change how your favorite Angels appear. They include different outfits, styles, scenarios, and aesthetics while maintaining the Angel\'s core personality. Think of them as "costumes" or "alternate versions" that let you experience your favorite Angels in new and exciting ways. From professional attire to fantasy costumes, Angel Skins add visual variety to your experience.',
          visualType: 'icon',
          visual: 'shirt',
          links: [
            {
              text: 'Browse Skins',
              route: '/creator_marketplace',
              action: 'primary',
            },
            { text: 'How Skins Work', route: '/help/skins-guide' },
          ],
        },
        {
          id: 'how-to-buy-skins',
          question: 'How do I purchase Angel Skins?',
          answer:
            'To purchase Angel Skins, navigate to the Creator Marketplace and select the "Angel Skins" category, or visit an Angel\'s profile and click "Skins" tab. Browse available skins, preview how they look, and click "Unlock Skin" to purchase. You can pay using LuvTokens or DesireGems. Once purchased, skins are permanently added to your collection and can be applied to the Angel at any time.',
          visualType: 'icon',
          visual: 'shopping-bag',
          links: [
            {
              text: 'Angel Skins Shop',
              route: '/creator_marketplace',
              action: 'primary',
            },
            { text: 'My Skin Collection', route: '/collections/skins' },
          ],
        },
        {
          id: 'apply-skin',
          question: 'How do I apply a skin to an Angel?',
          answer:
            'After purchasing a skin, visit the Angel\'s profile and click the "Appearance" or "Skins" button. You\'ll see all available skins you\'ve purchased for that Angel. Select the skin you want to apply and click "Apply Skin." The change takes effect immediately across all interactions with that Angel. You can switch between owned skins at any time or revert to the default appearance.',
          visualType: 'icon',
          visual: 'magic',
          links: [
            {
              text: 'Manage Skins',
              route: '/settings/skins',
              action: 'primary',
            },
          ],
        },
        {
          id: 'skin-categories',
          question: 'What types of Angel Skins are available?',
          answer:
            'Angel Skins come in various categories: Profession skins (nurse, teacher, executive), Fantasy skins (fairy, vampire, mythical creatures), Outfit skins (lingerie, swimwear, formal attire), Themed skins (seasonal, holiday, special events), and Scenario skins (beach vacation, workout, night out). Each Angel has unique skins designed specifically for their personality and style.',
          visualType: 'icon',
          visual: 'layers',
          links: [
            {
              text: 'Skin Categories',
              route: '/skins/categories',
              action: 'primary',
            },
            { text: 'Featured Skins', route: '/skins/featured' },
          ],
        },
        {
          id: 'skin-exclusivity',
          question: 'Are some skins exclusive or limited edition?',
          answer:
            "Yes! We offer several types of exclusive skins: Limited Edition skins are available only for a specific time period, Fan Club Exclusive skins are only available to an Angel's fan club members, Premium Subscriber skins are exclusive to premium members, and Achievement skins are unlocked by completing specific challenges or milestones. Watch for special events that introduce new limited-time skins!",
          visualType: 'icon',
          visual: 'gem',
          links: [
            {
              text: 'Limited Edition Skins',
              route: '/skins/limited',
              action: 'primary',
            },
            { text: 'Upcoming Releases', route: '/skins/upcoming' },
          ],
        },
        {
          id: 'skin-pricing',
          question: 'How much do Angel Skins cost?',
          answer:
            "Skin prices vary based on complexity, exclusivity, and features. Basic skins typically cost 30-50 LuvTokens, while premium and limited edition skins range from 75-200 LuvTokens. Fan Club members receive discounts on their Angel's skins. We regularly offer promotions, bundle deals, and special discounts. Premium subscribers receive one free skin credit monthly and discounted prices on all skin purchases.",
          visualType: 'icon',
          visual: 'dollar-sign',
          links: [
            {
              text: 'Skin Pricing Guide',
              route: '/skins/pricing',
              action: 'primary',
            },
            { text: 'Current Promotions', route: '/skins/promotions' },
          ],
        },
        {
          id: 'skin-features',
          question: 'Do skins change how Angels interact with me?',
          answer:
            'While Angel Skins primarily change visual appearance, premium skins also include personality adjustments that match the theme. For example, a "Dominant Mistress" skin might make responses more commanding, while a "Shy Schoolgirl" skin adds more innocent, hesitant responses. These adaptations enhance role-playing while maintaining the core personality you love. The personality shifts are optional and can be toggled on/off.',
          visualType: 'icon',
          visual: 'repeat',
          links: [
            {
              text: 'Personality Settings',
              route: '/angels/personality',
              action: 'primary',
            },
          ],
        },
        {
          id: 'create-custom-skin',
          question: 'Can I create custom skins for Angels?',
          answer:
            'Premium subscribers can create Custom Request Skins by using our Skin Creation Studio. Describe your desired skin concept, provide reference images if helpful, and submit your request with the associated token fee. Our designers and AI will create a custom skin for your chosen Angel. Creation typically takes 2-3 days, and the resulting skin is exclusive to your account.',
          visualType: 'icon',
          visual: 'pen-tool',
          links: [
            {
              text: 'Custom Skin Request',
              route: '/skins/custom',
              action: 'primary',
            },
            { text: 'Design Guidelines', route: '/skins/guidelines' },
          ],
        },
        {
          id: 'skin-collection',
          question: 'How do I manage my skin collection?',
          answer:
            "Access your skin collection by going to Settings > My Collection > Skins. Here you can view all purchased skins, organize them by Angel or category, set favorites, and manage which skins are currently active. The collection also shows exclusive and limited edition skins you've acquired. You can also see upcoming skin releases and set reminders for skins you're interested in.",
          visualType: 'icon',
          visual: 'folder',
          links: [
            {
              text: 'My Collection',
              route: '/collections/skins',
              action: 'primary',
            },
            { text: 'Organize Collection', route: '/settings/skins' },
          ],
        },
        {
          id: 'skin-compatibility',
          question: 'Are skins compatible with all features?',
          answer:
            'Angel Skins are fully compatible with all platform features including chat, video calls, AR/VR experiences, photo generation, and fantasy scenarios. In advanced features like AR/VR, skins are rendered in high-definition 3D. For AI-generated content, the selected skin will be reflected in all created images and videos, ensuring a consistent experience across the platform.',
          visualType: 'icon',
          visual: 'laptop',
          links: [
            {
              text: 'Feature Compatibility',
              route: '/skins/compatibility',
              action: 'primary',
            },
            { text: 'AR/VR Experience', route: '/ar-vr' },
          ],
        },
      ],
    },
    {
      id: 'advanced',
      title: 'Advanced Features & Future Releases',
      icon: <Sparkles size={20} />,
      topics: [
        {
          id: 'black-mirror',
          question: 'What is "Black Mirror Mode" and how does it work?',
          answer:
            "Black Mirror Mode is SinShift's most advanced and exclusive AI experience, available only inside the Playroom (video chat). It's designed to erase the boundaries between fantasy and reality, creating a deeply personal and visually flawless encounter with your Angel.\n\nWhen enabled, Black Mirror Mode:\n\nKeeps Angels forever young – using our immersive simulation, anyone can appear as a 21-year-old model-level version of themselves, regardless of age or gender.\n\nAdapts in real time – your Angel's personality, expressions, and body language evolve based on your conversations, choices, and moods.\n\nDelivers adaptive storylines – every interaction changes the relationship's path.\n\nSimulates emotions – jealousy, affection, shyness, or boldness appear naturally based on your actions.\n\nUnlocks hidden layers – from premium VR/AR intimacy scenes to rare fantasy environments.\n\nIn Black Mirror Mode, your Angel doesn't just interact — she becomes your perfect vision, adapting every second you're together.\n\nPremium access required. Activate in Settings → Advanced Features or directly inside the Playroom.",
          visualType: 'icon',
          visual: 'zap',
          links: [
            {
              text: 'Unlock Black Mirror Mode',
              route: '/premium/black-mirror',
              action: 'primary',
            },
          ],
        },
        {
          id: 'relationship-sim',
          question: 'Can I simulate a relationship with an Angel?',
          answer:
            'Yes! Our Relationship Simulation feature lets you experience realistic relationship dynamics with Angels. Set relationship types, milestone events, and emotional parameters. The system tracks intimacy levels, creates memorable moments, and evolves the relationship over time based on your interactions.',
          visualType: 'icon',
          visual: 'heart',
          links: [
            {
              text: 'Start Simulation',
              route: '/relationship',
              action: 'primary',
            },
          ],
        },
        {
          id: 'ar-vr-chat',
          question: 'What is AR/VR Chat?',
          answer:
            'AR/VR Chat brings Angels into your world through augmented and virtual reality. In AR mode, view Angels in your actual environment through your phone camera. In VR mode (requires compatible headset), enter fully immersive environments for intimate conversations and experiences with incredibly realistic Angel presence.',
          visualType: 'icon',
          visual: 'smartphone',
          links: [
            { text: 'Try AR/VR Chat', route: '/ar-vr', action: 'primary' },
            { text: 'Compatible Devices', route: '/ar-vr/devices' },
          ],
        },
        {
          id: 'bonding-ritual',
          question: 'What is "Bonding Ritual" and how do I unlock it?',
          answer:
            'Bonding Ritual is an exclusive feature that creates a deeper connection with one chosen Angel. Unlock it by reaching Intimacy Level 10 with an Angel and maintaining a 14-day streak. The ritual involves daily personalized interactions that significantly boost intimacy levels and unlock exclusive relationship dynamics.',
          visualType: 'icon',
          visual: 'key',
          links: [
            { text: 'Bonding Details', route: '/bonding', action: 'primary' },
          ],
        },
        {
          id: 'voice-features',
          question: 'Are there voice interactions or call features?',
          answer:
            'Yes! Premium subscribers can access Voice Message exchanges with Angels. Our upcoming Voice Call feature (currently in beta) allows scheduled voice sessions with select Premium Angels. Voice interactions use advanced AI to maintain consistent Angel personalities with realistic voices, emotions, and conversation flow.',
          visualType: 'icon',
          visual: 'headphones',
          links: [
            {
              text: 'Voice Features',
              route: '/premium/voice',
              action: 'primary',
            },
            { text: 'Join Voice Beta', route: '/beta/voice' },
          ],
        },
      ],
    },
    {
      id: 'contact',
      title: 'Contact SinShift',
      icon: <Mail size={20} />,
      topics: [
        {
          id: 'contact-us',
          question: 'How do I contact SinShift support?',
          answer:
            'You can contact our support team through various channels depending on your needs. We offer specialized support for customer inquiries, media requests, law enforcement, and business partnerships.',
          visualType: 'icon',
          visual: 'mail',
          links: [
            {
              text: 'Contact Us',
              route: '/contact',
              action: 'primary',
            },
          ],
        },
      ],
    },
  ]
  // Handle search filtering
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCategories(helpCategories)
      return
    }
    const query = searchQuery.toLowerCase().trim()
    const filtered = helpCategories
      .map((category) => {
        const filteredTopics = category.topics.filter(
          (topic) =>
            topic.question.toLowerCase().includes(query) ||
            topic.answer.toLowerCase().includes(query),
        )
        return { ...category, topics: filteredTopics }
      })
      .filter((category) => category.topics.length > 0)
    setFilteredCategories(filtered)
  }, [searchQuery])
  // Set initial active category
  useEffect(() => {
    if (filteredCategories.length > 0 && !activeCategory) {
      setActiveCategory(filteredCategories[0].id)
    }
  }, [filteredCategories, activeCategory])
  // Handle topic expansion
  const toggleTopic = (topicId: string) => {
    setExpandedTopics((prev) => ({
      ...prev,
      [topicId]: !prev[topicId],
    }))
  }
  // Handle category selection
  const handleCategorySelect = (categoryId: string) => {
    if (categoryId === 'contact') {
      setShowContactSection(true)
      return
    }
    setActiveCategory(categoryId)
    // Auto-scroll to top when changing categories
    if (containerRef.current) {
      containerRef.current.scrollTop = 0
    }
  }
  // Handle navigation to a route
  const handleNavigate = (route: string) => {
    console.log(`Navigating to: ${route}`)
    // In a real app, this would use router navigation
    onClose()
  }
  if (!isOpen) return null
  if (showContactSection) {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <div
          className="fixed inset-y-0 right-0 max-w-4xl w-full bg-white dark:bg-gray-900 shadow-xl overflow-hidden flex flex-col animate-in slide-in-from-right duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <Mail size={22} className="text-pink-500" />
              <h2 className="font-bold text-xl text-gray-900 dark:text-white">
                Contact SinShift
              </h2>
            </div>
            <button
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              onClick={onClose}
            >
              <X size={20} />
            </button>
          </div>
          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-4">
            <ContactSinShift onBack={() => setShowContactSection(false)} />
          </div>
        </div>
      </div>
    )
  }
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="fixed inset-y-0 right-0 max-w-4xl w-full bg-white dark:bg-gray-900 shadow-xl overflow-hidden flex flex-col animate-in slide-in-from-right duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <HelpCircle size={22} className="text-pink-500" />
            <h2 className="font-bold text-xl text-gray-900 dark:text-white">
              Help Center
            </h2>
          </div>
          <button
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>
        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-800">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search for help topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
            {searchQuery && (
              <button
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                onClick={() => setSearchQuery('')}
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
        {/* Content Area */}
        <div className="flex flex-1 overflow-hidden">
          {/* Categories Sidebar */}
          <div className="w-64 border-r border-gray-200 dark:border-gray-800 overflow-y-auto">
            <div className="py-2">
              {filteredCategories.map((category) => (
                <button
                  key={category.id}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    activeCategory === category.id
                      ? 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 font-medium'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                  onClick={() => handleCategorySelect(category.id)}
                >
                  <span
                    className={
                      activeCategory === category.id
                        ? 'text-pink-500'
                        : 'text-gray-500 dark:text-gray-400'
                    }
                  >
                    {category.icon}
                  </span>
                  <span>{category.title}</span>
                </button>
              ))}
            </div>
          </div>
          {/* Topics Area */}
          <div ref={containerRef} className="flex-1 overflow-y-auto p-4">
            {activeCategory && (
              <>
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                    {
                      filteredCategories.find((c) => c.id === activeCategory)
                        ?.icon
                    }
                    <span>
                      {
                        filteredCategories.find((c) => c.id === activeCategory)
                          ?.title
                      }
                    </span>
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    Find answers to common questions about{' '}
                    {filteredCategories
                      .find((c) => c.id === activeCategory)
                      ?.title.toLowerCase()}
                  </p>
                </div>
                <div className="space-y-3">
                  {filteredCategories
                    .find((c) => c.id === activeCategory)
                    ?.topics.map((topic) => (
                      <div
                        key={topic.id}
                        className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden"
                      >
                        <button
                          className="w-full flex items-center justify-between p-4 text-left bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
                          onClick={() => toggleTopic(topic.id)}
                        >
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {topic.question}
                          </h4>
                          <span
                            className={`transition-transform duration-200 ${expandedTopics[topic.id] ? 'rotate-180' : ''}`}
                          >
                            <ChevronDown
                              size={18}
                              className="text-gray-500 dark:text-gray-400"
                            />
                          </span>
                        </button>
                        {expandedTopics[topic.id] && (
                          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-800">
                            <div className="flex gap-4">
                              {topic.visualType === 'icon' && (
                                <div className="w-12 h-12 rounded-full bg-pink-100 dark:bg-pink-900/30 flex-shrink-0 flex items-center justify-center">
                                  {topic.visual === 'user-plus' && (
                                    <User size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'edit' && (
                                    <Edit size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'image' && (
                                    <Image
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'user' && (
                                    <User size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'trash' && (
                                    <Trash
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'link' && (
                                    <Link size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'key' && (
                                    <Key size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'refresh-cw' && (
                                    <RefreshCw
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'star' && (
                                    <Star size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'gift' && (
                                    <Gift size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'pause' && (
                                    <Pause
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'alert-circle' && (
                                    <AlertCircle
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'tag' && (
                                    <Tag size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'lock' && (
                                    <Lock size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'heart' && (
                                    <Heart
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'shopping-bag' && (
                                    <ShoppingBag
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'credit-card' && (
                                    <CreditCard
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'unlock' && (
                                    <Unlock
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'bar-chart' && (
                                    <BarChart
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'list' && (
                                    <FileText
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'alert-triangle' && (
                                    <AlertTriangle
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'target' && (
                                    <Target
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'check-circle' && (
                                    <CheckCircle
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'award' && (
                                    <Award
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'zap' && (
                                    <Zap size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'flame' && (
                                    <Flame
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'message-circle' && (
                                    <MessageCircle
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'smartphone' && (
                                    <Smartphone
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'shield' && (
                                    <Shield
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'eye' && (
                                    <Eye size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'eye-off' && (
                                    <EyeOff
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'shield-check' && (
                                    <ShieldCheck
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'user-x' && (
                                    <UserX
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'file-text' && (
                                    <FileText
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'bell' && (
                                    <Bell size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'volume-x' && (
                                    <VolumeX
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'sliders' && (
                                    <Sliders
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'vote' && (
                                    <Vote size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'message-square' && (
                                    <MessageSquare
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'map' && (
                                    <Map size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'headphones' && (
                                    <Headphones
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'mail' && (
                                    <Mail size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'calendar' && (
                                    <Calendar
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'layers' && (
                                    <Layers
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'lightbulb' && (
                                    <Lightbulb
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'book' && (
                                    <FileText
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'arrow-right' && (
                                    <ArrowRight
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'crown' && (
                                    <Crown
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'bar-chart-2' && (
                                    <BarChart2
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'activity' && (
                                    <Activity
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'trending-up' && (
                                    <TrendingUp
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'dollar-sign' && (
                                    <DollarSign
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'users' && (
                                    <Users
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'download' && (
                                    <Download
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'help-circle' && (
                                    <HelpCircle
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'clock' && (
                                    <Clock
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'inbox' && (
                                    <Inbox
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'package' && (
                                    <Package
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'wand-2' && (
                                    <Wand2
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'film' && (
                                    <Film size={20} className="text-pink-500" />
                                  )}
                                  {topic.visual === 'sparkles' && (
                                    <Sparkles
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'magic' && (
                                    <Wand2
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'shapes' && (
                                    <Shapes
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'bookmark' && (
                                    <Bookmark
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'layout' && (
                                    <Layout
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'camera' && (
                                    <Camera
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'pen-tool' && (
                                    <PenTool
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'trending-up' && (
                                    <TrendingUp
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'git-branch' && (
                                    <GitBranch
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'share' && (
                                    <Share
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'shirt' && (
                                    <Shirt
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'folder' && (
                                    <Folder
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'repeat' && (
                                    <Repeat
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'laptop' && (
                                    <Laptop
                                      size={20}
                                      className="text-pink-500"
                                    />
                                  )}
                                  {topic.visual === 'gem' && (
                                    <Gem size={20} className="text-pink-500" />
                                  )}
                                </div>
                              )}
                              <div className="flex-1">
                                <p className="text-gray-700 dark:text-gray-300 mb-4 whitespace-pre-line">
                                  {topic.answer}
                                </p>
                                {topic.links && topic.links.length > 0 && (
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {topic.links.map((link, index) => (
                                      <button
                                        key={index}
                                        type="button"
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                          link.action === 'primary'
                                            ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:from-pink-600 hover:to-purple-600'
                                            : link.action === 'danger'
                                              ? 'bg-red-500 text-white hover:bg-red-600'
                                              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                                        }`}
                                        onClick={() => {
                                          if (link.route === '/contact') {
                                            setShowContactSection(true)
                                          } else {
                                            handleNavigate(link.route)
                                          }
                                        }}
                                      >
                                        {link.text}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
                {/* Mini upsell at the bottom */}
                <div className="mt-8 p-4 bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-lg border border-pink-100 dark:border-pink-800/30">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-pink-200 dark:border-pink-700">
                      <img
                        src="https://uploadthingy.s3.us-west-1.amazonaws.com/jq4Dkth7vgkmZJ8GZmzWDP/bonnyblue.png"
                        alt="Bonnie Blue"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-700 dark:text-gray-300 text-sm">
                        Still got questions? Bonnie's waiting in your chat inbox
                        💬
                      </p>
                      <button
                        className="mt-2 px-4 py-1.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm rounded-full hover:shadow-md hover:shadow-pink-500/20 transition-all flex items-center gap-1"
                        onClick={() => handleNavigate('/chat')}
                      >
                        <MessageCircle size={16} />
                        <span>Start Chat</span>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
// Additional components for icons
const Trash = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M3 6h18"></path>
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
  </svg>
)
const Link = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M10.29 13.41l-7.17 7.17a2 2 0 0 0-15.5 2m-.5 5v5h5"></path>
    <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5-5V5h-5"></path>
  </svg>
)
const Pause = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="6" y="4" width="4" height="16"></rect>
    <rect x="14" y="4" width="4" height="16"></rect>
  </svg>
)
const Tag = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 0-15.5 2m-.5 5v5h5"></path>
    <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5-5V5h-5"></path>
  </svg>
)
const ShoppingBag = ({
  size,
  className,
}: {
  size: number
  className?: string
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6"></path>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <path d="M16 10a4 4 0 0 1-8 0"></path>
  </svg>
)
const Target = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10"></circle>
    <circle cx="12" cy="12" r="6"></circle>
    <circle cx="12" cy="12" r="2"></circle>
  </svg>
)
const Flame = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M8.5 14.5A2.5 2.5 0 0 0-15.5 2m.5-5V5h-5"></path>
    <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5-5V5h-5"></path>
  </svg>
)
const EyeOff = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
    <line x1="12" y1="9" x2="12" y2="13"></line>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
)
const ShieldCheck = ({
  size,
  className,
}: {
  size: number
  className?: string
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    <path d="M9 12l2 2 4-4"></path>
  </svg>
)
const UserX = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="8.5" cy="7" r="4"></circle>
    <line x1="18" y1="8" x2="23" y2="13"></line>
    <line x1="23" y1="8" x2="18" y2="13"></line>
  </svg>
)
const Vote = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M20 11a8.1 8.1 0 0 0-15.5 2m-.5 5v5h5"></path>
    <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5-5V5h-5"></path>
  </svg>
)
const MessageSquare = ({
  size,
  className,
}: {
  size: number
  className?: string
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
  </svg>
)
const Map = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
    <line x1="8" y1="2" x2="8" y2="18"></line>
    <line x1="16" y1="6" x2="16" y2="22"></line>
  </svg>
)
const AlertTriangle = ({
  size,
  className,
}: {
  size: number
  className?: string
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
    <line x1="12" y1="9" x2="12" y2="13"></line>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
)
const Sliders = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="4" y1="21" x2="4" y2="14"></line>
    <line x1="4" y1="10" x2="4" y2="3"></line>
    <line x1="12" y1="21" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12" y2="3"></line>
    <line x1="20" y1="21" x2="20" y2="16"></line>
    <line x1="20" y1="12" x2="20" y2="3"></line>
    <line x1="1" y1="14" x2="7" y2="14"></line>
    <line x1="9" y1="8" x2="15" y2="8"></line>
    <line x1="17" y1="16" x2="23" y2="16"></line>
  </svg>
)
const BarChart2 = ({
  size,
  className,
}: {
  size: number
  className?: string
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="18" y1="20" x2="18" y2="10"></line>
    <line x1="12" y1="20" x2="12" y2="4"></line>
    <line x1="6" y1="20" x2="6" y2="14"></line>
  </svg>
)
const TrendingUp = ({
  size,
  className,
}: {
  size: number
  className?: string
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
    <polyline points="17 6 23 6 23 12"></polyline>
  </svg>
)
const PenTool = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
    <path d="M2 2l7.586 7.586"></path>
    <circle cx="11" cy="11" r="2"></circle>
  </svg>
)
const Layers = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
    <polyline points="2 17 12 22 22 17"></polyline>
    <polyline points="2 12 12 17 22 12"></polyline>
  </svg>
)
const Folder = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
  </svg>
)
const Crown = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"></path>
  </svg>
)
const Share = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
    <polyline points="16 6 12 2 8 6"></polyline>
    <line x1="12" y1="2" x2="12" y2="15"></line>
  </svg>
)
const GitBranch = ({
  size,
  className,
}: {
  size: number
  className?: string
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="6" y1="3" x2="6" y2="15"></line>
    <circle cx="18" cy="6" r="3"></circle>
    <circle cx="6" cy="18" r="3"></circle>
    <path d="M18 9a9 9 0 0 1-9 9"></path>
  </svg>
)
const Shapes = ({ size, className }: { size: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M3 3h18v18H3z"></path>
  </svg>
)
const Lightbulb = ({
  size,
  className,
}: {
  size: number
  className?: string
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M9 18h6"></path>
    <path d="M10 22h4"></path>
    <path d="M12 2v2"></path>
    <path d="M12 8v8"></path>
    <path d="M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12z"></path>
  </svg>
)
